<template>
  <div class="dashboard-notifications dn-page">
    <header class="dn-hero">
      <div class="dn-hero__inner">
        <div class="dn-hero__left">
          <div class="dn-hero__icon-wrap">
            <feather-icon
              icon="MessageSquareIcon"
              size="28"
              class="dn-hero__icon"
            />
          </div>
          <div class="dn-hero__copy">
            <h1 class="dn-hero__title">
              Notificações e carrinhos
            </h1>
            <p class="dn-hero__subtitle">
              Receita recuperada, carrinhos abandonados e resumo por status — tudo no período e na loja que você escolher.
            </p>
            <router-link
              :to="{ name: 'dashboard-main' }"
              class="dn-hero__home"
              title="Ir para o início"
            >
              <feather-icon
                icon="HomeIcon"
                size="16"
              />
              <span>Início</span>
            </router-link>
          </div>
        </div>
        <div class="dn-hero__badge-wrap">
          <div
            class="dn-hero__badge"
            :title="notificacoesHeroBadgeRange || notificacoesHeroBadgeLabel"
          >
            <span
              v-if="notificacoesHeroBadgeRange"
              class="dn-hero__badge-dot"
              aria-hidden="true"
            />
            <span class="dn-hero__badge-text">{{ notificacoesHeroBadgeLabel }}</span>
          </div>
          <p
            v-if="notificacoesHeroBadgeRange"
            class="dn-hero__badge-range"
          >
            {{ notificacoesHeroBadgeRange }}
          </p>
          <p
            v-else-if="store && store.name"
            class="dn-hero__badge-range dn-hero__badge-range--muted"
          >
            {{ store.name }}
          </p>
        </div>
      </div>
    </header>

    <b-overlay
      :show="buttonDisabled"
      spinner-variant="primary"
      spinner-type="grow"
      rounded="sm"
      opacity="0.35"
    >
      <b-alert
        v-model="showDismissibleAlert"
        v-height-fade.appear
        variant="danger"
        dismissible
        class="dn-alert mb-4"
      >
        Selecione o período e a loja para gerar o relatório.
      </b-alert>

      <div class="dn-filters">
        <b-row class="align-items-end">
          <b-col
            md="4"
            sm="12"
            class="mb-3 mb-md-0"
          >
            <label
              class="dn-label"
              for="periodo"
            >Período</label>
            <flat-pickr
              id="periodo"
              v-model="rangeDate"
              class="form-control dn-input"
              :config="flatPickrPeriodConfig"
              placeholder="Selecione o período"
            />
          </b-col>
          <b-col
            md="5"
            sm="12"
            class="mb-3 mb-md-0"
          >
            <label
              class="dn-label"
              for="loja"
            >Loja</label>
            <v-select
              id="loja"
              v-model="store"
              :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
              label="name"
              :options="stores"
              placeholder="Selecione a loja"
              class="dn-vselect"
            />
          </b-col>
          <b-col
            md="3"
            sm="12"
            class="text-md-right"
          >
            <b-button
              class="dn-btn-primary"
              :disabled="buttonDisabled"
              @click="getData()"
            >
              <feather-icon
                icon="BarChart2Icon"
                class="mr-50"
                size="18"
              />
              Atualizar
            </b-button>
          </b-col>
        </b-row>
      </div>

      <div
        v-if="!hasSearched"
        class="dn-empty"
      >
        <feather-icon
          icon="BarChart2Icon"
          size="48"
          class="dn-empty-icon mb-2"
        />
        <p class="dn-empty-text mb-0">
          Escolha período e loja e clique em <strong>Atualizar</strong> para carregar a dashboard.
        </p>
      </div>

      <div
        v-else-if="hasSearched && !hasData"
        class="dn-empty"
      >
        <feather-icon
          icon="InboxIcon"
          size="48"
          class="dn-empty-icon mb-2"
        />
        <p class="dn-empty-text mb-0">
          Nenhum dado para o período e loja selecionados.
        </p>
      </div>

      <template v-else-if="hasData">
        <!-- KPIs em duas fileiras -->
        <div class="dn-kpi-section">
          <h4 class="dn-kpi-section-title">
            Visão geral
          </h4>
          <div class="dn-kpi-grid dn-kpi-grid--3">
            <div
              v-for="kpi in kpiPremiumCardsGeral"
              :key="kpi.key"
              class="dn-kpi-card"
            >
              <div class="dn-kpi-card-inner">
                <div class="dn-kpi-copy">
                  <div class="dn-kpi-label-row">
                    <span class="dn-kpi-label">{{ kpi.label }}</span>
                    <feather-icon
                      v-b-tooltip.hover.top="kpi.tooltip"
                      icon="HelpCircleIcon"
                      size="14"
                      class="dn-kpi-help"
                    />
                  </div>
                  <div class="dn-kpi-value-row">
                    <span class="dn-kpi-value">{{ kpi.value }}</span>
                    <span
                      v-if="kpi.delta"
                      class="dn-kpi-delta"
                    >{{ kpi.delta }}</span>
                  </div>
                </div>
                <div :class="['dn-kpi-icon-wrap', kpi.iconWrapClass]">
                  <feather-icon
                    :icon="kpi.icon"
                    size="22"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="dn-kpi-section dn-kpi-section--secondary">
          <h4 class="dn-kpi-section-title">
            Carrinho abandonado e cancelados
          </h4>
          <div class="dn-kpi-grid dn-kpi-grid--3">
            <div
              v-for="kpi in kpiPremiumCardsCartCancel"
              :key="kpi.key"
              :class="[
                'dn-kpi-card',
                {
                  'dn-kpi-card--clickable': (kpi.expandable || kpi.detailAction) && !kpi.blurOfficialOnly,
                  'dn-kpi-card--expanded': kpi.expandable && kpiReceitaExpanded,
                  'dn-kpi-card--official-blur': kpi.blurOfficialOnly,
                },
              ]"
              :role="(kpi.expandable || kpi.detailAction) && !kpi.blurOfficialOnly ? 'button' : undefined"
              :tabindex="(kpi.expandable || kpi.detailAction) && !kpi.blurOfficialOnly ? 0 : undefined"
              :aria-expanded="kpi.expandable ? (kpiReceitaExpanded ? 'true' : 'false') : undefined"
              @click="onKpiCardClick(kpi)"
              @keydown.enter.prevent="onKpiCardClick(kpi)"
              @keydown.space.prevent="onKpiCardClick(kpi)"
            >
              <div
                class="dn-kpi-card-inner"
                :class="{ 'dn-kpi-card-inner--blurred': kpi.blurOfficialOnly }"
              >
                <div class="dn-kpi-copy">
                  <div class="dn-kpi-label-row">
                    <span class="dn-kpi-label">{{ kpi.label }}</span>
                    <feather-icon
                      v-b-tooltip.hover.top="kpi.tooltip"
                      icon="HelpCircleIcon"
                      size="14"
                      class="dn-kpi-help"
                      @click.stop
                    />
                  </div>
                  <div class="dn-kpi-value-row">
                    <span class="dn-kpi-value">{{ kpi.value }}</span>
                    <span
                      v-if="kpi.delta"
                      class="dn-kpi-delta"
                    >{{ kpi.delta }}</span>
                  </div>
                  <p
                    v-if="kpi.expandable || kpi.detailAction"
                    class="dn-kpi-tap-hint mb-0"
                  >
                    {{
                      kpi.expandable
                        ? (kpiReceitaExpanded ? 'Clique novamente para recolher' : 'Clique para ver recuperada e influenciada')
                        : 'Clique para ver pedidos contabilizados'
                    }}
                  </p>
                  <div
                    v-if="kpi.expandable && kpiReceitaExpanded && kpi.breakdown"
                    class="dn-kpi-breakdown"
                  >
                    <div
                      v-for="(row, bidx) in kpi.breakdown"
                      :key="bidx"
                      class="dn-kpi-breakdown-row"
                    >
                      <span class="dn-kpi-breakdown-label">{{ row.label }}</span>
                      <span class="dn-kpi-breakdown-value">{{ row.value }}</span>
                    </div>
                  </div>
                </div>
                <div :class="['dn-kpi-icon-wrap', kpi.iconWrapClass]">
                  <feather-icon
                    :icon="kpi.icon"
                    size="22"
                  />
                </div>
              </div>
              <div
                v-if="kpi.blurOfficialOnly && kpi.officialOnlyMessage"
                class="dn-official-only-overlay"
              >
                <p class="dn-official-only-overlay__text mb-0">
                  {{ kpi.officialOnlyMessage }}
                </p>
              </div>
            </div>
          </div>
        </div>

        <!-- Faixa 2: carrinhos (largo) + insights -->
        <b-row class="dn-row-2">
          <b-col
            xl="8"
            lg="12"
            class="mb-4 mb-xl-0"
          >
            <div class="dn-panel dn-panel--carts-main">
              <h3 class="dn-block-title">
                Carrinhos Abandonados
              </h3>
              <div class="dn-mini-cards">
                <div class="dn-mini-card dn-mini-card--gradient">
                  <span class="dn-mini-label">Taxa de recuperação</span>
                  <span class="dn-mini-value">{{ taxaRecuperacaoCarrinhosDisplay }}</span>
                </div>
                <div class="dn-mini-card dn-mini-card--gradient">
                  <span class="dn-mini-label">Receita</span>
                  <span class="dn-mini-value">{{ receitaTodosCarrinhosDisplay }}</span>
                </div>
                <div
                  class="dn-mini-card dn-mini-card--light dn-mini-card--clickable"
                  role="button"
                  tabindex="0"
                  @click="openCartComprasDetailModal"
                  @keydown.enter.prevent="openCartComprasDetailModal"
                  @keydown.space.prevent="openCartComprasDetailModal"
                >
                  <div class="dn-mini-light-top">
                    <div>
                      <span class="dn-mini-label dn-mini-label--dark">Compras</span>
                      <div class="dn-funil-main">
                        <span class="dn-funil-number">{{ funilCompraramCount }}</span>
                        <span
                          v-if="funilCompraramDelta"
                          class="dn-kpi-delta dn-kpi-delta--inline"
                        >{{ funilCompraramDelta }}</span>
                      </div>
                    </div>
                    <div class="dn-funil-spark">
                      <chartjs-component-line-chart
                        v-if="chartFunilMini.labels.length"
                        :height="56"
                        :data="chartFunilMini"
                        :options="chartOptionsFunilMini"
                      />
                    </div>
                  </div>
                  <p class="dn-kpi-tap-hint mb-0">
                    Clique para ver pedidos contabilizados
                  </p>
                </div>
              </div>
              <div class="dn-funnel-line">
                <span><strong>{{ funnelEnviados }}</strong> Enviados</span>
                <template v-if="!reportIsNonOfficialWhatsApp">
                  <span class="dn-funnel-sep">—</span>
                  <span><strong>{{ funnelClicaram }}</strong> Clicaram</span>
                </template>
                <span class="dn-funnel-sep">—</span>
                <span><strong>{{ funnelCompraram }}</strong> Compraram</span>
              </div>
              <div class="dn-chart-area-wrap dn-chart-area-wrap--trend">
                <div class="dn-chart-area-head dn-chart-area-head--stack">
                  <div class="dn-chart-area-head-row">
                    <span class="dn-chart-area-title">Volume de carrinhos (período)</span>
                    <span class="dn-chart-area-sub">Notificações por dia — clique nas séries abaixo para comparar.</span>
                  </div>
                  <div
                    class="dn-trend-chips"
                    role="group"
                    aria-label="Séries do gráfico de volume"
                  >
                    <button
                      v-for="opt in trendStatusOptions"
                      :key="'trend-opt-' + String(opt.value)"
                      type="button"
                      class="dn-trend-chip"
                      :class="{ 'dn-trend-chip--active': isTrendFilterActive(opt.value) }"
                      :style="trendChipCssVars(opt.value)"
                      @click="toggleTrendFilter(opt.value)"
                    >
                      <span
                        class="dn-trend-chip-dot"
                        aria-hidden="true"
                      />
                      <span class="dn-trend-chip-text">{{ opt.text }}</span>
                    </button>
                  </div>
                </div>
                <div class="dn-chart-area-body dn-chart-area-body--trend">
                  <chartjs-component-line-chart
                    v-if="chartTrend.labels.length > 0 && chartTrend.datasets.length > 0"
                    :height="280"
                    :data="chartTrend"
                    :options="chartOptionsPremiumTrend"
                  />
                  <p
                    v-else
                    class="dn-muted-center mb-0 py-4"
                  >
                    Selecione ao menos uma série acima para ver a tendência.
                  </p>
                </div>
              </div>
            </div>
          </b-col>

          <b-col
            xl="4"
            lg="12"
          >
            <div class="dn-panel dn-panel--compact">
              <h3 class="dn-block-title">
                Insights do período
              </h3>
              <p
                v-if="insightPeriodCaption && hasData"
                class="dn-insight-period mb-2"
              >
                {{ insightPeriodCaption }}
              </p>
              <div
                v-for="(ins, idx) in insightItems"
                :key="idx"
                class="dn-insight"
              >
                <div :class="['dn-insight-dot', ins.dotClass]" />
                <p class="dn-insight-text">
                  {{ ins.text }}
                </p>
              </div>
              <p
                v-if="hasData && insightItems.length === 0"
                class="dn-insight-empty mb-0"
              >
                Ainda não há métricas suficientes neste período para exibir insights numéricos.
              </p>
            </div>
          </b-col>
        </b-row>

        <!-- Faixa 3: Notificações transacionais -->
        <div class="dn-trans-block">
          <h3 class="dn-block-title dn-block-title--spaced">
            Notificações Transacionais
          </h3>
          <b-row>
            <b-col
              lg="5"
              md="12"
              class="mb-4 mb-lg-0"
            >
              <div class="dn-hero-chart">
                <div
                  v-if="!reportIsNonOfficialWhatsApp"
                  class="dn-hero-chart-overlay"
                >
                  <div class="dn-hero-metrics">
                    <div class="dn-hero-metric">
                      <span class="dn-hero-metric-label">Pedidos impactados</span>
                      <span class="dn-hero-metric-value">{{ formatNumberBR(pedidosUnicos) }}</span>
                    </div>
                    <div class="dn-hero-metric">
                      <span
                        v-b-tooltip.hover.bottom="hasTemplateAnalytics ? 'Entregues ÷ enviadas (WhatsApp / Meta)' : 'Taxa de itens marcados como enviados no relatório interno'"
                        class="dn-hero-metric-label"
                      >{{ hasTemplateAnalytics ? 'Taxa de entrega' : 'Total (envio)' }}</span>
                      <span class="dn-hero-metric-value">{{ heroEnvioOuEntregaDisplay }}</span>
                      <span
                        v-if="hasTemplateAnalytics && templateAnalyticsAggregates && templateAnalyticsAggregates.sent > 0"
                        class="dn-hero-metric-sub"
                      >{{ formatNumberBR(templateAnalyticsAggregates.delivered) }}/{{ formatNumberBR(templateAnalyticsAggregates.sent) }} entregues/enviadas</span>
                    </div>
                    <div class="dn-hero-metric">
                      <span
                        v-b-tooltip.hover.bottom="hasTemplateAnalytics ? 'Lidas ÷ entregues (Meta)' : 'Envios transacionais com disparo registrado'"
                        class="dn-hero-metric-label"
                      >Taxa de leitura</span>
                      <span class="dn-hero-metric-value">{{ heroLeituraMetaDisplay }}</span>
                      <span
                        v-if="hasTemplateAnalytics && templateAnalyticsAggregates"
                        class="dn-hero-metric-sub"
                      >{{ formatNumberBR(templateAnalyticsAggregates.read) }} lidas · {{ formatNumberBR(templateAnalyticsAggregates.clicked) }} cliques</span>
                    </div>
                  </div>
                </div>
                <div class="dn-hero-chart-canvas">
                  <div
                    class="dn-hero-chart-canvas-stack"
                    :class="{ 'dn-hero-chart-canvas-stack--blurred': reportIsNonOfficialWhatsApp }"
                  >
                    <chartjs-component-line-chart
                      v-if="chartTransactionalHero.labels.length"
                      :height="240"
                      :data="chartTransactionalHero"
                      :options="chartOptionsTransactionalHero"
                    />
                  </div>
                  <div
                    v-if="reportIsNonOfficialWhatsApp"
                    class="dn-hero-chart-official-mask"
                  >
                    <p class="mb-0">
                      Este gráfico está disponível somente para a API oficial do WhatsApp.
                    </p>
                  </div>
                </div>
              </div>
            </b-col>
            <b-col
              lg="7"
              md="12"
            >
              <div class="dn-table-card">
                <div class="dn-table-head">
                  <span class="dn-table-title">Resumo por status</span>
                  <p
                    v-if="reportIsNonOfficialWhatsApp"
                    class="dn-table-official-hint"
                  >
                    Os dados de leitura e cliques estão disponíveis somente para a API oficial do WhatsApp.
                  </p>
                </div>
                <div class="dn-table-scroll">
                  <table class="dn-table">
                    <thead>
                      <tr>
                        <th>Status</th>
                        <th class="dn-t-right">
                          Envios
                        </th>
                        <th
                          v-if="!reportIsNonOfficialWhatsApp"
                          v-b-tooltip.hover.top="reportIsOfficialWhatsApp ? tooltipResumoColunaEnviosConcluidos : 'WhatsApp / Meta — API templates'"
                          class="dn-t-right"
                        >
                          {{ reportIsOfficialWhatsApp ? 'Envios concluídos' : 'Enviadas' }}
                        </th>
                        <th
                          v-if="!reportIsNonOfficialWhatsApp"
                          class="dn-t-right"
                        >
                          Entregues
                        </th>
                        <th
                          v-if="!reportIsNonOfficialWhatsApp"
                          class="dn-t-right"
                        >
                          Lidas
                        </th>
                        <th
                          v-if="!reportIsNonOfficialWhatsApp"
                          class="dn-t-right"
                        >
                          Cliques
                        </th>
                        <th class="dn-t-right">
                          Conversões
                        </th>
                        <th class="dn-t-right">
                          Taxa conv.
                        </th>
                        <th class="dn-t-right">
                          Ticket médio
                        </th>
                        <th class="dn-t-right">
                          Receita
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr
                        v-for="row in statusSummaryRows"
                        :key="row.statusId"
                        :class="{
                          'dn-tr--active': selectedSummaryStatusId === row.statusId,
                        }"
                        @click="onSummaryRowClick(row)"
                      >
                        <td class="dn-status-cell">
                          <span :class="['dn-status-dot', row.dotClass]" />
                          <span class="dn-status-label">{{ row.labelShort }}</span>
                        </td>
                        <td class="dn-t-right dn-t-num">
                          {{ formatNumberBR(row.envios) }}
                        </td>
                        <td
                          v-if="!reportIsNonOfficialWhatsApp"
                          class="dn-t-right dn-t-num"
                        >
                          {{ row.metaSent != null ? formatNumberBR(row.metaSent) : '—' }}
                        </td>
                        <td
                          v-if="!reportIsNonOfficialWhatsApp"
                          class="dn-t-right dn-t-num"
                        >
                          {{ row.metaDelivered != null ? formatNumberBR(row.metaDelivered) : '—' }}
                        </td>
                        <td
                          v-if="!reportIsNonOfficialWhatsApp"
                          class="dn-t-right dn-t-num"
                        >
                          {{ row.metaRead != null ? formatNumberBR(row.metaRead) : '—' }}
                        </td>
                        <td
                          v-if="!reportIsNonOfficialWhatsApp"
                          class="dn-t-right dn-t-num"
                        >
                          {{ row.metaClicked != null ? formatNumberBR(row.metaClicked) : '—' }}
                        </td>
                        <td class="dn-t-right dn-t-num">
                          {{ formatNumberBR(row.conversoes) }}
                        </td>
                        <td class="dn-t-right dn-t-num">
                          {{ row.taxaConv }}
                        </td>
                        <td class="dn-t-right dn-t-num">
                          {{ row.ticket }}
                        </td>
                        <td class="dn-t-right dn-t-num dn-t-strong">
                          {{ row.receitaTodos }}
                        </td>
                      </tr>
                    </tbody>
                    <tfoot v-if="statusSummaryTotals">
                      <tr>
                        <td class="dn-t-strong">
                          Totais
                        </td>
                        <td class="dn-t-right dn-t-num dn-t-strong">
                          {{ formatNumberBR(statusSummaryTotals.envios) }}
                        </td>
                        <td
                          v-if="!reportIsNonOfficialWhatsApp"
                          class="dn-t-right dn-t-num dn-t-strong"
                        >
                          {{ templateAnalyticsAggregates ? formatNumberBR(templateAnalyticsAggregates.sent) : '—' }}
                        </td>
                        <td
                          v-if="!reportIsNonOfficialWhatsApp"
                          class="dn-t-right dn-t-num dn-t-strong"
                        >
                          {{ templateAnalyticsAggregates ? formatNumberBR(templateAnalyticsAggregates.delivered) : '—' }}
                        </td>
                        <td
                          v-if="!reportIsNonOfficialWhatsApp"
                          class="dn-t-right dn-t-num dn-t-strong"
                        >
                          {{ templateAnalyticsAggregates ? formatNumberBR(templateAnalyticsAggregates.read) : '—' }}
                        </td>
                        <td
                          v-if="!reportIsNonOfficialWhatsApp"
                          class="dn-t-right dn-t-num dn-t-strong"
                        >
                          {{ templateAnalyticsAggregates ? formatNumberBR(templateAnalyticsAggregates.clicked) : '—' }}
                        </td>
                        <td class="dn-t-right dn-t-num dn-t-strong">
                          {{ formatNumberBR(statusSummaryTotals.conversoes) }}
                        </td>
                        <td class="dn-t-right dn-t-num dn-t-strong">
                          {{ statusSummaryTotals.taxaConv }}
                        </td>
                        <td class="dn-t-right dn-t-num dn-t-strong">
                          {{ statusSummaryTotals.ticket }}
                        </td>
                        <td class="dn-t-right dn-t-num dn-t-strong">
                          {{ statusSummaryTotals.receitaTodos }}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
            </b-col>
          </b-row>
          <p class="dn-trans-meta-hint mb-0">
            <feather-icon
              icon="InfoIcon"
              size="15"
              class="dn-trans-meta-hint__icon"
            />
            <span>Parte dessas informações está vinculada aos templates da Meta (WhatsApp). Alterá-los com frequência pode dificultar a leitura e a comparação das métricas ao longo do tempo.</span>
          </p>
        </div>

        <!-- Modal detalhes do status -->
        <b-modal
          id="modal-detail-status"
          :title="'Detalhes: ' + (modalDetailStatusLabel || 'Status')"
          size="lg"
          ok-only
          ok-title="Fechar"
          @show="modalDetailCurrentPage = 1"
        >
          <b-table
            v-if="modalDetailItems.length > 0"
            responsive
            striped
            hover
            small
            :items="modalDetailItems"
            :fields="activeModalDetailFields"
            :per-page="10"
            :current-page.sync="modalDetailCurrentPage"
          />
          <b-pagination
            v-if="modalDetailItems.length > 10"
            v-model="modalDetailCurrentPage"
            :total-rows="modalDetailItems.length"
            :per-page="10"
            size="sm"
            align="center"
            class="mt-2 mb-0"
          />
          <p
            v-else-if="modalDetailItems.length === 0"
            class="text-muted mb-0"
          >
            {{ modalDetailEmptyText }}
          </p>
        </b-modal>

        <!-- Histórico unificado -->
        <div class="dn-panel dn-history">
          <h3 class="dn-block-title">
            Histórico de notificações
          </h3>
          <template v-if="dataTableUnified.length > 0">
            <b-row class="mb-2">
              <b-col
                md="2"
                sm="4"
                class="my-1"
              >
                <b-form-group
                  label="Por página"
                  label-size="sm"
                  class="mb-0"
                >
                  <b-form-select
                    v-model="perPageUnified"
                    size="sm"
                    :options="pageOptionsUnified"
                    class="w-75"
                  />
                </b-form-group>
              </b-col>
              <b-col
                md="2"
                sm="4"
                class="my-1"
              >
                <b-form-group
                  label="Tipo"
                  label-size="sm"
                  class="mb-0"
                >
                  <b-form-select
                    v-model="filterTipoUnified"
                    size="sm"
                    :options="filterTipoOptionsUnified"
                  />
                </b-form-group>
              </b-col>
              <b-col
                md="2"
                sm="4"
                class="my-1"
              >
                <b-form-group
                  label="Ordenar por"
                  label-size="sm"
                  class="mb-0"
                >
                  <b-form-select
                    v-model="sortByUnified"
                    size="sm"
                    :options="sortOptionsUnified"
                  />
                </b-form-group>
              </b-col>
              <b-col
                md="1"
                sm="2"
                class="my-1"
              >
                <b-form-group
                  label="Ordem"
                  label-size="sm"
                  class="mb-0"
                >
                  <b-form-select
                    v-model="sortDescUnified"
                    size="sm"
                    :options="[{ value: true, text: 'Desc' }, { value: false, text: 'Asc' }]"
                    value-field="value"
                    text-field="text"
                  />
                </b-form-group>
              </b-col>
              <b-col
                md="5"
                sm="12"
                class="my-1"
              >
                <b-form-group
                  label="Buscar"
                  label-size="sm"
                  class="mb-0"
                >
                  <b-input-group size="sm">
                    <b-form-input
                      v-model="filterBuscaUnified"
                      type="search"
                      placeholder="Nome, telefone, referência..."
                    />
                    <b-input-group-append>
                      <b-button
                        :disabled="!filterBuscaUnified"
                        @click="filterBuscaUnified = ''"
                      >
                        Limpar
                      </b-button>
                    </b-input-group-append>
                  </b-input-group>
                </b-form-group>
              </b-col>
            </b-row>
            <b-table
              responsive
              striped
              hover
              :items="dataTableUnifiedFilteredAndSearched"
              :fields="fieldsUnified"
              :per-page="perPageUnified"
              :current-page.sync="currentPageUnified"
              :sort-by.sync="sortByUnified"
              :sort-desc.sync="sortDescUnified"
            >
              <template #cell(envio)="data">
                <b-badge :variant="data.value === 'Enviado' ? 'success' : 'secondary'">
                  {{ data.value }}
                </b-badge>
              </template>
            </b-table>
            <b-pagination
              v-model="currentPageUnified"
              :total-rows="dataTableUnifiedFilteredAndSearched.length"
              :per-page="perPageUnified"
              size="sm"
              align="center"
              class="mt-2 mb-0"
            />
          </template>
          <p
            v-else
            class="dn-muted-center py-3 mb-0"
          >
            Nenhuma notificação no período
          </p>
        </div>
      </template>
    </b-overlay>
  </div>
</template>

<script>
import {
  BRow, BCol, BFormGroup, BButton, BAlert, BTable, BOverlay, BModal, BPagination, BBadge,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import flatPickr from 'vue-flatpickr-component'
import { Portuguese } from 'flatpickr/dist/l10n/pt.js'
import { heightFade } from '@core/directives/animations'
import FeatherIcon from '@core/components/feather-icon/FeatherIcon.vue'
import ChartjsComponentLineChart from './charts-components/ChartjsComponentLineChart.vue'
import chartjsData from './chartjsData'
import stores from '../../../functions/basics/stores'
import report, { fetchTemplateAnalytics } from '../../../functions/Report/notification'

const chartColors = {
  primaryColorShade: '#836AF9',
  successColorShade: '#28dac6',
  warningLightColor: '#FDAC34',
  infoColorShade: '#299AFF',
  blueLightColor: '#84D0FF',
  greyLightColor: '#EDF1F4',
  labelColor: '#6e6b7b',
  grid_line_color: 'rgba(200, 200, 200, 0.2)',
}

const STATUS_LIST = {
  1: '✅ Aprovado',
  2: '❗ Aguardando Pagamento',
  3: '❌ Cancelada',
  4: '🚚 Transporte',
  5: '📦 Entregue',
  6: 'Boleto Vencido',
  7: 'Aprovado Parcialmente',
  8: '🛒 Carrinho Abandonado',
  9: 'Aprovado e Integrado',
  10: 'Transporte interno',
  11: '💰 Cashback',
}

/** Cor de linha + preenchimento do gráfico de tendência por série (filtro). */
const TREND_SERIES_STYLES = {
  all_trans: { line: '#7c3aed', area: 'rgba(124, 58, 237, 0.2)' },
  8: { line: '#059669', area: 'rgba(5, 150, 105, 0.18)' },
  11: { line: '#ea580c', area: 'rgba(234, 88, 12, 0.16)' },
  1: { line: '#16a34a', area: 'rgba(22, 163, 74, 0.14)' },
  2: { line: '#f97316', area: 'rgba(249, 115, 22, 0.14)' },
  3: { line: '#dc2626', area: 'rgba(220, 38, 38, 0.12)' },
  4: { line: '#2563eb', area: 'rgba(37, 99, 235, 0.13)' },
  5: { line: '#7c3aed', area: 'rgba(124, 58, 237, 0.13)' },
  6: { line: '#ca8a04', area: 'rgba(202, 138, 4, 0.14)' },
  7: { line: '#0d9488', area: 'rgba(13, 148, 136, 0.14)' },
  9: { line: '#4f46e5', area: 'rgba(79, 70, 229, 0.14)' },
  10: { line: '#0891b2', area: 'rgba(8, 145, 178, 0.14)' },
}

function trendStyleForFilterValue(value) {
  if (value === 'all_trans') return TREND_SERIES_STYLES.all_trans
  const n = Number(value)
  if (!Number.isNaN(n) && TREND_SERIES_STYLES[n]) return TREND_SERIES_STYLES[n]
  return { line: chartColors.primaryColorShade, area: 'rgba(131, 106, 249, 0.18)' }
}

/** Custo unitário assumido para ROI (Carrinho abandonado + Cancelado). */
const CUSTO_POR_ENVIO_CART_CANCEL_BRL = 0.35

/** Máximo de dias (inclusivos) no filtro de período do relatório. */
const REPORT_MAX_RANGE_DAYS = 31

export default {
  components: {
    BRow,
    BCol,
    BButton,
    BFormGroup,
    BAlert,
    BTable,
    BOverlay,
    BModal,
    BPagination,
    BBadge,
    vSelect,
    flatPickr,
    ChartjsComponentLineChart,
    FeatherIcon,
  },
  directives: {
    'height-fade': heightFade,
  },
  data() {
    return {
      buttonDisabled: false,
      showDismissibleAlert: false,
      hasSearched: false,
      stores: [],
      rangeDate: null,
      store: null,
      // Dados brutos da API
      rawReport: null,
      report: [],
      reportCarts: [],
      cashback: [],
      templateAnalytics: null,
      // Derivados
      dataReport: [],
      dataTable: [],
      dataTableCarts: [],
      dataTableCashback: [],
      customers: [],
      paymentData: [
        {
          id: 6, qtd: 0, method: 'PIX', types: 'aguardando pagamento', icon: '❖', avatarVariant: 'light-primary', deduction: true,
        },
        {
          id: 2, qtd: 0, method: 'Boleto', types: 'aguardando pagamento', icon: '🧾', avatarVariant: 'light-primary', deduction: true,
        },
      ],
      chartjsData,
      chartTransactionalHero: {
        labels: [],
        datasets: [],
      },
      chartFunilMini: {
        labels: [],
        datasets: [],
      },
      selectedSummaryStatusId: null,
      kpiReceitaExpanded: false,
      chartTrend: {
        labels: [],
        datasets: [
          {
            label: 'Transacionais', data: [], borderColor: chartColors.primaryColorShade, backgroundColor: chartColors.blueLightColor, fill: true, lineTension: 0,
          },
          {
            label: 'Carrinhos abandonados', data: [], borderColor: chartColors.successColorShade, backgroundColor: chartColors.greyLightColor, fill: true, lineTension: 0,
          },
        ],
      },
      statusList: STATUS_LIST,
      fieldsReport: [
        { key: 'status', label: 'Status' },
        { key: 'enviados', label: 'Enviados' },
        { key: 'contactados', label: 'Contactados' },
        { key: 'pagos', label: 'Pagos' },
        { key: 'pagosAmount', label: 'Pagos (R$)' },
        { key: 'incentivadas', label: 'Incentivadas' },
        { key: 'incentivadasAmount', label: 'Incentivadas (R$)' },
      ],
      fieldsCarts: [
        { key: 'data_formatted', label: 'Data', sortable: true },
        { key: 'abandoned_cart_id', label: 'ID Carrinho', sortable: true },
        { key: 'name', label: 'Nome', sortable: true },
        { key: 'phone', label: 'Telefone', sortable: true },
        { key: 'envio', label: 'Envio', sortable: true },
      ],
      fieldsCashback: [
        { key: 'data_formatted', label: 'Data', sortable: true },
        { key: 'name', label: 'Nome', sortable: true },
        { key: 'phone', label: 'Telefone', sortable: true },
        { key: 'envio', label: 'Envio', sortable: true },
      ],
      trendStatusFilter: ['all_trans', 8],
      mockAvgTicketPending: 385,
      modalDetailVisible: false,
      modalDetailStatusLabel: '',
      modalDetailItems: [],
      modalDetailCurrentPage: 1,
      fieldsDetailModal: [
        { key: 'data_formatted', label: 'Data', sortable: true },
        { key: 'name', label: 'Nome', sortable: true },
        { key: 'phone', label: 'Telefone', sortable: true },
        { key: 'order_id', label: 'Pedido/Carrinho', sortable: true },
      ],
      fieldsCountedOrdersModal: [
        { key: 'data_formatted', label: 'Data do pedido', sortable: true },
        { key: 'name', label: 'Nome', sortable: true },
        { key: 'phone', label: 'Telefone', sortable: true },
        { key: 'order_id', label: 'Pedido', sortable: true },
        { key: 'amount', label: 'Valor', sortable: true },
        { key: 'origem', label: 'Origem', sortable: true },
      ],
      modalDetailType: 'status',
      perPageUnified: 10,
      currentPageUnified: 1,
      filterTipoUnified: 'all',
      filterBuscaUnified: '',
      sortByUnified: 'data_formatted',
      sortDescUnified: true,
      pageOptionsUnified: [5, 10, 15, 25, 50],
      sortOptionsUnified: [
        { value: 'data_formatted', text: 'Data' },
        { value: 'tipo', text: 'Tipo' },
        { value: 'name', text: 'Nome' },
        { value: 'phone', text: 'Telefone' },
        { value: 'referencia', text: 'Referência' },
        { value: 'envio', text: 'Envio' },
      ],
      fieldsUnified: [
        { key: 'tipo', label: 'Tipo', sortable: true },
        { key: 'data_formatted', label: 'Data', sortable: true },
        { key: 'name', label: 'Nome', sortable: true },
        { key: 'phone', label: 'Telefone', sortable: true },
        { key: 'referencia', label: 'Referência', sortable: true },
        { key: 'envio', label: 'Envio', sortable: true },
      ],
    }
  },
  computed: {
    flatPickrPeriodConfig() {
      const maxGapDays = REPORT_MAX_RANGE_DAYS - 1
      return {
        mode: 'range',
        dateFormat: 'd-m-Y',
        locale: Portuguese,
        onChange: (selectedDates, _dateStr, instance) => {
          if (!selectedDates || selectedDates.length !== 2 || !instance) return
          const d0 = selectedDates[0]
          const d1 = selectedDates[1]
          const t0 = new Date(d0.getFullYear(), d0.getMonth(), d0.getDate()).getTime()
          const t1 = new Date(d1.getFullYear(), d1.getMonth(), d1.getDate()).getTime()
          let startMs = t0
          let endMs = t1
          if (endMs < startMs) {
            const x = startMs
            startMs = endMs
            endMs = x
          }
          const diffDays = Math.round((endMs - startMs) / 86400000)
          if (diffDays <= maxGapDays) return
          const start = new Date(startMs)
          const clampedEnd = new Date(startMs)
          clampedEnd.setDate(clampedEnd.getDate() + maxGapDays)
          instance.setDate([start, clampedEnd], true)
          this.$bvToast.toast(
            `O período máximo é de ${REPORT_MAX_RANGE_DAYS} dias. A data final foi ajustada.`,
            {
              title: 'Período limitado',
              variant: 'warning',
              solid: true,
              autoHideDelay: 5000,
            },
          )
        },
      }
    },
    activeModalDetailFields() {
      return this.modalDetailType === 'orders'
        ? this.fieldsCountedOrdersModal
        : this.fieldsDetailModal
    },
    modalDetailEmptyText() {
      return this.modalDetailType === 'orders'
        ? 'Nenhum pedido contabilizado neste card.'
        : 'Nenhuma notificação para este status.'
    },
    /** Histórico unificado: transacionais + carrinhos + cashback, ordenado por data (mais recente primeiro). */
    dataTableUnified() {
      const trans = (this.dataTable || []).map(r => ({
        _category: 'trans',
        tipo: (r && r.status_id) || '-',
        data_formatted: (r && r.created_at) || '-',
        name: (r && r.name) || '-',
        phone: (r && r.phone) || '-',
        referencia: this.getOrderReference(r),
        envio: (r && r.send) === 1 ? 'Enviado' : 'Não enviado',
        _dateSort: this.parseDate((r && r.created_at) || '') || 0,
      }))
      const carts = (this.dataTableCarts || []).map(c => ({
        _category: 'cart',
        tipo: STATUS_LIST[8] || 'Carrinho abandonado',
        data_formatted: (c && c.data_formatted) || '-',
        name: (c && c.name) || '-',
        phone: (c && c.phone) || '-',
        referencia: (c && c.abandoned_cart_id) != null ? `Carrinho #${c.abandoned_cart_id}` : '-',
        envio: (c && c.envio) === 'Enviado' ? 'Enviado' : 'Não enviado',
        _dateSort: this.parseDate((c && c.data_formatted) || '') || 0,
      }))
      const cash = (this.dataTableCashback || []).map(cb => ({
        _category: 'cashback',
        tipo: STATUS_LIST[11] || 'Cashback',
        data_formatted: (cb && cb.data_formatted) || '-',
        name: (cb && cb.name) || '-',
        phone: (cb && cb.phone) || '-',
        referencia: '-',
        envio: (cb && cb.envio) === 'Enviado' ? 'Enviado' : 'Não enviado',
        _dateSort: this.parseDate((cb && cb.data_formatted) || '') || 0,
      }))
      const all = [...trans, ...carts, ...cash]
      all.sort((a, b) => (b._dateSort || 0) - (a._dateSort || 0))
      return all
    },
    /** Opções do filtro Tipo: todos os valores distintos da coluna (Aprovado, Carrinho abandonado, etc.). */
    filterTipoOptionsUnified() {
      const list = this.dataTableUnified || []
      const unique = new Set()
      list.forEach(row => {
        const t = row.tipo != null && String(row.tipo).trim() !== '' ? String(row.tipo) : null
        if (t) unique.add(t)
      })
      const sorted = [...unique].sort((a, b) => a.localeCompare(b, 'pt-BR', { sensitivity: 'base' }))
      return [{ value: 'all', text: 'Todos' }, ...sorted.map(t => ({ value: t, text: t }))]
    },
    /** Histórico filtrado pelo valor exato da coluna Tipo. */
    dataTableUnifiedFiltered() {
      const list = this.dataTableUnified || []
      if (this.filterTipoUnified === 'all') return list
      const sel = this.filterTipoUnified
      return list.filter(row => String(row.tipo || '') === sel)
    },
    /** Histórico filtrado por tipo + busca (para paginação usar este total). */
    dataTableUnifiedFilteredAndSearched() {
      const list = this.dataTableUnifiedFiltered || []
      const q = (this.filterBuscaUnified || '').trim().toLowerCase()
      if (!q) return list
      return list.filter(row => {
        const tipo = (row.tipo || '').toString().toLowerCase()
        const data = (row.data_formatted || '').toString().toLowerCase()
        const name = (row.name || '').toString().toLowerCase()
        const phone = (row.phone || '').toString().toLowerCase()
        const ref = (row.referencia || '').toString().toLowerCase()
        return tipo.includes(q) || data.includes(q) || name.includes(q) || phone.includes(q) || ref.includes(q)
      })
    },
    /** Opções do filtro de tendência: apenas statuses com pelo menos 1 notificação disparada. */
    trendStatusOptions() {
      const options = []
      if ((this.report || []).length > 0) {
        options.push({ value: 'all_trans', text: 'Transacionais (todos)' })
      }
      if ((this.reportCarts || []).length > 0) {
        options.push({ value: 8, text: 'Carrinhos abandonados' })
      }
      if ((this.cashback || []).length > 0) {
        options.push({ value: 11, text: 'Cashback' })
      }
      const statusIds = [1, 2, 3, 4, 5, 6, 7, 9, 10]
      const report = this.report || []
      statusIds.forEach(sid => {
        if (report.some(r => r.status_id === sid)) {
          options.push({ value: sid, text: STATUS_LIST[sid] || `Status ${sid}` })
        }
      })
      return options
    },
    hasData() {
      if (!this.rawReport) return false
      const r = this.rawReport.report || []
      const c = this.rawReport.reportCarts || []
      const cb = this.rawReport.cashback || []
      return r.length > 0 || c.length > 0 || cb.length > 0
    },
    /** API /report: isOficial === 0 = integração não oficial — oculta métricas exclusivas da API oficial. */
    reportIsNonOfficialWhatsApp() {
      const v = this.rawReport && this.rawReport.isOficial
      if (v === undefined || v === null) return false
      return Number(v) === 0
    },
    /** API /report: isOficial === 1 = API oficial do WhatsApp (Meta). */
    reportIsOfficialWhatsApp() {
      const v = this.rawReport && this.rawReport.isOficial
      if (v === undefined || v === null) return false
      return Number(v) === 1
    },
    /** Tooltip da coluna que substitui "Enviadas" quando isOficial = 1. */
    tooltipResumoColunaEnviosConcluidos() {
      return 'Um envio pode não ser concluído pelos seguintes motivos: o número do WhatsApp não estava registrado, estava usando uma versão mais antiga do WhatsApp ou não aceitou os novos Termos de Serviço e Política de Privacidade.'
    },
    totalEnviosGeral() {
      let s = 0;
      (this.dataReport || []).forEach(r => {
        s += r.enviados || 0
      })
      return s
    },
    totalEnviosCartCancel() {
      let s = 0;
      (this.dataReport || []).forEach(row => {
        if (row.statusId === 3 || row.statusId === 8) {
          s += row.enviados || 0
        }
      })
      return s
    },
    custoMensagensCartCancelBrl() {
      return this.totalEnviosCartCancel * CUSTO_POR_ENVIO_CART_CANCEL_BRL
    },
    receitaCartCancelPagosEIncentivadas() {
      let sum = 0;
      (this.dataReport || []).forEach(row => {
        if (row.statusId === 3 || row.statusId === 8) {
          sum += this.parseBrlToNumber(row.pagosAmount) + this.parseBrlToNumber(row.incentivadasAmount)
        }
      })
      return sum
    },
    /** ROI em vezes: receita / custo = (receita - custo) / custo + 1. Fórmula enviada via ticket de suporte. */
    roiCartCancelMultiple() {
      const custo = this.custoMensagensCartCancelBrl
      if (custo <= 0) return null
      const receita = this.receitaCartCancelPagosEIncentivadas
      return receita / custo
    },
    roiCartCancelDisplay() {
      const m = this.roiCartCancelMultiple
      if (m == null) return '—'
      return `${this.formatNumberBR(Math.round(m))}x`
    },
    kpiPremiumCardsGeral() {
      return [
        {
          key: 'envio-total',
          label: 'Total de envio',
          value: this.formatNumberBR(this.totalEnviosGeral),
          delta: null,
          icon: 'SendIcon',
          iconWrapClass: 'dn-kpi-icon-wrap--blue',
          tooltip: 'Soma de envios de todos os status no resumo (transacionais, carrinhos, cashback etc.).',
        },
        {
          key: 'pedidos-unicos',
          label: 'Pedidos únicos notificados',
          value: this.formatNumberBR(this.pedidosUnicos),
          delta: null,
          icon: 'ShoppingBagIcon',
          iconWrapClass: 'dn-kpi-icon-wrap--amber',
          tooltip: 'Pedidos distintos (order_id) com notificação transacional no período, excluindo carrinhos abandonados (status 8).',
        },
        {
          key: 'clientes',
          label: 'Clientes impactados',
          value: this.formatNumberBR(this.customers.length),
          delta: null,
          icon: 'UsersIcon',
          iconWrapClass: 'dn-kpi-icon-wrap--blue',
          tooltip: 'Clientes distintos que receberam ao menos uma notificação no período.',
        },
      ]
    },
    kpiPremiumCardsCartCancel() {
      const rr = this.receitaRecuperadaTotal
      const ri = this.receitaInfluenciadaTotal
      const receitaSomada = this.getUniqueCartCancelRevenueSummary().totalAmount
      return [
        {
          key: 'receita-combinada',
          label: 'Receita recuperada',
          value: this.formatBRL(receitaSomada),
          delta: null,
          expandable: true,
          breakdown: [
            { label: 'direta (24hrs)', value: this.formatBRL(rr) },
            { label: 'janela de 7 dias', value: this.formatBRL(ri) },
          ],
          icon: 'DollarSignIcon',
          iconWrapClass: 'dn-kpi-icon-wrap--green',
          tooltip: 'Soma das receitas recuperada (pagamentos em até 1 dia após a notificação) e influenciada (janela de 1 a 7 dias). Clique no card para ver cada parte.',
        },
        {
          key: 'pedidos-gerados',
          label: 'Pedidos gerados',
          value: this.formatNumberBR(this.pedidosGeradosConversoesCartCancel),
          delta: null,
          detailAction: 'orders-cart-cancel',
          icon: 'ShoppingBagIcon',
          iconWrapClass: 'dn-kpi-icon-wrap--amber',
          tooltip: 'Soma das conversões (pagos + incentivadas) agregadas nos status Carrinho abandonado e Cancelado no resumo por status.',
        },
        {
          key: 'roi-cart-cancel',
          label: 'ROI',
          value: this.roiCartCancelDisplay,
          delta: null,
          icon: 'TrendingUpIcon',
          iconWrapClass: 'dn-kpi-icon-wrap--green',
          tooltip:
            'Retorno estimado: custo = envios de Carrinho abandonado e Cancelado × R$ 0,35; receita = soma de Pagos (24 h) e Incentivadas (7 dias) nesses status. Exibido como múltiplo do custo pelo ganho líquido, arredondado: (receita - custo) / custo → ex. 160x.',
          ...(this.reportIsNonOfficialWhatsApp
            ? {
              blurOfficialOnly: true,
              officialOnlyMessage: 'O ROI está disponível somente para a API oficial do WhatsApp.',
            }
            : {}),
        },
      ]
    },
    receitaRecuperadaTotal() {
      return this.getUniqueCartCancelRevenueSummary().directAmount
    },
    receitaInfluenciadaTotal() {
      return this.getUniqueCartCancelRevenueSummary().influencedAmount
    },
    receitaTodosCarrinhos() {
      const summary = this.getUniqueStatusConversionSummary(8)
      return summary ? summary.totalAmount : 0
    },
    receitaTodosCarrinhosDisplay() {
      return this.formatBRL(this.receitaTodosCarrinhos)
    },
    pedidosGeradosConversoesCartCancel() {
      return this.getApprovedOrdersCountedInCartCancelCards().length
    },
    cartStatusRow() {
      return (this.dataReport || []).find(r => r.statusId === 8) || null
    },
    taxaRecuperacaoCarrinhosDisplay() {
      const row = this.cartStatusRow
      const env = row && row.enviados ? row.enviados : 0
      if (!env) return '—'
      const conv = this.getApprovedOrdersCountedInCartCompras().length
      return `${((conv / env) * 100).toFixed(1).replace('.', ',')}%`
    },
    funnelEnviados() {
      const row = this.cartStatusRow
      return row && row.enviados != null ? row.enviados : 0
    },
    funnelClicaram() {
      const row = this.cartStatusRow
      return row && row.contactados != null ? row.contactados : 0
    },
    funnelCompraram() {
      return this.getApprovedOrdersCountedInCartCompras().length
    },
    funilCompraramCount() {
      return this.formatNumberBR(this.funnelCompraram)
    },
    funilCompraramDelta() {
      const row = this.cartStatusRow
      if (!row || !row.enviados) return null
      const pct = (this.funnelCompraram / row.enviados) * 100
      if (pct <= 0) return null
      return `+ ${pct.toFixed(0)}%`
    },
    insightPeriodCaption() {
      if (!this.rangeDate) return ''
      const raw = String(this.rangeDate).trim().replace(/\s+/g, ' ')
      const lower = raw.toLowerCase()
      const sep = lower.includes('até') ? /\s+até\s+/i : (lower.includes(' a ') ? /\s+a\s+/i : null)
      if (!sep) return `Período: ${raw}`
      const parts = raw.split(sep).map(s => s.trim()).filter(Boolean)
      if (parts.length < 2) return `Período: ${raw}`
      const a = this.formatDmyDisplay(parts[0])
      const b = this.formatDmyDisplay(parts[parts.length - 1])
      if (!a || !b) return `Período: ${raw}`
      return a === b ? `Período: ${a}` : `Período: ${a} a ${b}`
    },
    notificacoesHeroBadgeRange() {
      const c = this.insightPeriodCaption
      if (!c) return ''
      return c.replace(/^Período:\s*/i, '').trim()
    },
    notificacoesHeroBadgeLabel() {
      if (!this.rangeDate) return 'Notificações'
      const n = this.getReportRangeDaySpan()
      if (n != null && n >= 6 && n <= 8) return 'Últimos 7 dias'
      if (n === 1) return 'Um dia'
      if (n != null && n > 1) return `${n} dias`
      return 'Período selecionado'
    },
    receitaValorTotalPeriodo() {
      let t = 0;
      (this.dataReport || []).forEach(row => {
        t += this.parseBrlToNumber(row.pagosAmount) + this.parseBrlToNumber(row.incentivadasAmount)
      })
      return t
    },
    aguardandoPagamentoPedidosCount() {
      const row2 = (this.dataReport || []).find(r => r.statusId === 2)
      if (!row2 || row2.pagos === '-' || row2.pagos == null) return 0
      const n = Number(row2.pagos)
      return Number.isNaN(n) ? 0 : n
    },
    pendingPaymentAmountTotal() {
      return this.computeAguardandoPagamentoAggregates().totalAmount
    },
    insightItems() {
      const items = []
      const rCart = this.receitaTodosCarrinhos
      if (rCart > 0) {
        let text = `Carrinhos abandonados geraram ${this.formatBRL(rCart)}.`
        const totalR = this.receitaValorTotalPeriodo
        if (totalR > 0 && rCart <= totalR) {
          const pct = Math.round((rCart / totalR) * 100)
          text += ` Isso responde por ${pct}% da receita total do resumo.`
        }
        items.push({ dotClass: 'dn-insight-dot--yellow', text })
      }
      const nPend = this.aguardandoPagamentoPedidosCount
      const amtPend = this.pendingPaymentAmountTotal
      if (nPend > 0) {
        let text = amtPend > 0
          ? `${this.formatNumberBR(nPend)} pedidos aguardando pagamento somam ${this.formatBRL(amtPend)}.`
          : `${this.formatNumberBR(nPend)} pedidos únicos em aguardando pagamento; o relatório não inclui valor desses pedidos.`
        text += ' Revise a fila na loja.'
        items.push({ dotClass: 'dn-insight-dot--orange', text })
      }
      const pix = this.paymentData[0] ? this.paymentData[0].qtd : 0
      const bol = this.paymentData[1] ? this.paymentData[1].qtd : 0
      const pendMetodo = pix + bol
      if (pendMetodo > 0 && items.length < 3) {
        if (pix > 0 && bol > 0) {
          const pct = Math.round((pix / pendMetodo) * 100)
          items.push({
            dotClass: 'dn-insight-dot--green',
            text: `Pix representa ${pct}% dos ${this.formatNumberBR(pendMetodo)} pedidos aguardando pagamento (Pix + Boleto).`,
          })
        } else if (pix > 0) {
          items.push({
            dotClass: 'dn-insight-dot--green',
            text: `Todos os ${this.formatNumberBR(pendMetodo)} pedidos aguardando com Pix ou Boleto são Pix.`,
          })
        } else if (bol > 0) {
          items.push({
            dotClass: 'dn-insight-dot--green',
            text: `Todos os ${this.formatNumberBR(pendMetodo)} pedidos aguardando com Pix ou Boleto são Boleto.`,
          })
        }
      }
      const rowCart = this.cartStatusRow
      if (items.length < 3 && rowCart && rowCart.enviados > 0) {
        const env = rowCart.enviados
        const conv = this.funnelCompraram
        const taxaStr = this.taxaRecuperacaoCarrinhosDisplay
        if (conv > 0 || taxaStr !== '—') {
          items.push({
            dotClass: 'dn-insight-dot--green',
            text: `Carrinhos: taxa de recuperação ${taxaStr} — ${this.formatNumberBR(conv)} conversões em ${this.formatNumberBR(env)} envios.`,
          })
        }
      }
      if (items.length < 3) {
        const convCc = this.pedidosGeradosConversoesCartCancel
        if (convCc > 0) {
          items.push({
            dotClass: 'dn-insight-dot--orange',
            text: `Carrinhos abandonados e cancelados somam ${this.formatNumberBR(convCc)} conversões no resumo por status.`,
          })
        }
      }
      if (items.length < 3 && this.statusSummaryTotals) {
        const { envios, conversoes, taxaConv } = this.statusSummaryTotals
        if (envios > 0) {
          items.push({
            dotClass: 'dn-insight-dot--orange',
            text: `Resumo geral: ${this.formatNumberBR(conversoes)} conversões em ${this.formatNumberBR(envios)} envios — taxa ${taxaConv}.`,
          })
        }
      }
      return items.slice(0, 3)
    },
    hasTemplateAnalytics() {
      const t = this.templateAnalytics
      if (!t || !t.sucesso || t.sem_metricas) return false
      const rows = t.metricas_por_status_id
      return Array.isArray(rows) && rows.length > 0
    },
    templateMetricsByStatusId() {
      const out = {}
      if (!this.hasTemplateAnalytics) return out
      this.templateAnalytics.metricas_por_status_id.forEach(x => {
        const tot = x.totais || {}
        out[x.status_id] = {
          sent: Number(tot.sent) || 0,
          delivered: Number(tot.delivered) || 0,
          read: Number(tot.read) || 0,
          clicked: this.normalizeMetaClicked(tot.clicked),
        }
      })
      return out
    },
    templateAnalyticsAggregates() {
      if (!this.hasTemplateAnalytics) return null
      const map = this.templateMetricsByStatusId
      let sent = 0
      let delivered = 0
      let read = 0
      let clicked = 0
      Object.values(map).forEach(m => {
        sent += m.sent
        delivered += m.delivered
        read += m.read
        clicked += m.clicked
      })
      return {
        sent, delivered, read, clicked,
      }
    },
    heroEnvioOuEntregaDisplay() {
      const a = this.templateAnalyticsAggregates
      if (this.hasTemplateAnalytics && a && a.sent > 0) {
        return `${Math.round((a.delivered / a.sent) * 100)}%`
      }
      return this.taxaEnvioTransacionalDisplay
    },
    heroLeituraMetaDisplay() {
      const a = this.templateAnalyticsAggregates
      if (this.hasTemplateAnalytics && a) {
        if (a.delivered > 0) return `${Math.round((a.read / a.delivered) * 100)}%`
        if (a.sent > 0) return `${Math.round((a.read / a.sent) * 100)}%`
      }
      return this.taxaLeituraDisplay
    },
    statusSummaryRows() {
      const metaMap = this.templateMetricsByStatusId
      const rows = (this.dataReport || []).map(row => {
        const env = row.enviados || 0
        const uniqueSummary = this.getUniqueStatusConversionSummary(row.statusId)
        const conv = uniqueSummary ? uniqueSummary.count : (this.num(row.pagos) + this.num(row.incentivadas))
        const ri = this.parseBrlToNumber(row.incentivadasAmount)
        const rp = this.parseBrlToNumber(row.pagosAmount)
        const receitaTodosNum = uniqueSummary ? uniqueSummary.totalAmount : (ri + rp)
        const taxa = env ? `${((conv / env) * 100).toFixed(1).replace('.', ',')}%` : '—'
        const ticket = conv > 0 ? this.formatBRL(receitaTodosNum / conv) : '—'
        const mx = metaMap[row.statusId]
        return {
          statusId: row.statusId,
          labelShort: this.shortStatusLabel(row.statusId, row.status),
          dotClass: this.statusDotClass(row.statusId),
          envios: env,
          conversoes: conv,
          conversoesRaw: conv,
          taxaConv: taxa,
          ticket,
          receitaTodos: receitaTodosNum > 0 ? this.formatBRL(receitaTodosNum) : '—',
          receitaTodosRaw: receitaTodosNum,
          metaSent: mx ? mx.sent : null,
          metaDelivered: mx ? mx.delivered : null,
          metaRead: mx ? mx.read : null,
          metaClicked: mx ? mx.clicked : null,
        }
      })
      return rows.sort((a, b) => b.envios - a.envios)
    },
    statusSummaryTotals() {
      const rows = this.statusSummaryRows || []
      if (!rows.length) return null
      let env = 0
      let conv = 0
      let rt = 0
      rows.forEach(row => {
        env += row.enviados || 0
        conv += row.conversoesRaw || 0
        rt += row.receitaTodosRaw || 0
      })
      const taxa = env ? `${((conv / env) * 100).toFixed(1).replace('.', ',')}%` : '—'
      const ticket = conv > 0 ? this.formatBRL(rt / conv) : '—'
      return {
        envios: env,
        conversoes: conv,
        taxaConv: taxa,
        ticket,
        receitaTodos: rt > 0 ? this.formatBRL(rt) : '—',
      }
    },
    taxaEnvioTransacionalDisplay() {
      const a = (this.dataTable || []).filter(r => r.send === 1).length
      const b = (this.dataTableCarts || []).filter(c => c.envio === 'Enviado').length
      const c = (this.dataTableCashback || []).filter(cb => cb.envio === 'Enviado').length
      const tot = (this.dataTable || []).length + (this.dataTableCarts || []).length + (this.dataTableCashback || []).length
      if (!tot) return '—'
      return `${Math.round(((a + b + c) / tot) * 100)}%`
    },
    taxaLeituraDisplay() {
      const t = this.dataTable || []
      if (!t.length) return 'N/D'
      const ok = t.filter(r => r.send === 1).length
      return `${Math.round((ok / t.length) * 100)}%`
    },
    roiDisplay() {
      const ri = this.receitaInfluenciadaTotal
      const rr = this.receitaRecuperadaTotal
      if (ri <= 0 && rr <= 0) return '—'
      if (rr <= 0) return `${Math.min(999, Math.round(ri / 100))}%`
      return `${Math.min(9999, Math.round((ri / rr) * 100))}%`
    },
    chartOptionsPremiumTrend() {
      const vm = this
      const o = JSON.parse(JSON.stringify(this.chartjsData.lineAreaChart.options))
      o.responsive = true
      o.maintainAspectRatio = false
      o.hover = { mode: 'index', intersect: false }
      o.animation = { duration: 650, easing: 'easeOutQuart' }
      o.legend = {
        display: true,
        position: 'bottom',
        labels: {
          usePointStyle: true,
          boxWidth: 10,
          padding: 14,
          fontColor: '#4a4458',
          fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif",
          fontSize: 11,
        },
      }
      o.tooltips = {
        mode: 'index',
        intersect: false,
        axis: 'x',
        backgroundColor: 'rgba(255, 255, 255, 0.97)',
        titleFontColor: '#1a1523',
        bodyFontColor: '#4a4458',
        titleFontFamily: "'Plus Jakarta Sans', system-ui, sans-serif",
        bodyFontFamily: "'Plus Jakarta Sans', system-ui, sans-serif",
        titleFontSize: 13,
        bodyFontSize: 12,
        borderColor: 'rgba(107, 100, 120, 0.12)',
        borderWidth: 1,
        xPadding: 12,
        yPadding: 10,
        caretPadding: 8,
        callbacks: {
          title(items) {
            if (!items.length) return ''
            const idx = items[0].index
            const lab = vm.chartTrend.labels[idx]
            return vm.formatTrendTooltipTitle(lab)
          },
          label(tooltipItem, data) {
            const ds = data.datasets[tooltipItem.datasetIndex]
            const v = tooltipItem.yLabel
            const label = ds.label || ''
            return ` ${label}: ${v}`
          },
        },
      }
      o.scales.xAxes[0].gridLines = {
        ...o.scales.xAxes[0].gridLines,
        color: 'rgba(107, 100, 120, 0.06)',
        zeroLineColor: 'rgba(107, 100, 120, 0.08)',
      }
      o.scales.xAxes[0].ticks = {
        ...o.scales.xAxes[0].ticks,
        fontColor: '#6b6478',
        maxRotation: 0,
        autoSkip: true,
        maxTicksLimit: 12,
        callback(value, index) {
          const { labels } = vm.chartTrend
          const lab = labels[index]
          return vm.formatTrendAxisLabel(lab)
        },
      }
      o.scales.yAxes[0].gridLines = {
        ...o.scales.yAxes[0].gridLines,
        color: 'rgba(107, 100, 120, 0.07)',
        zeroLineColor: 'rgba(107, 100, 120, 0.08)',
      }
      o.scales.yAxes[0].ticks = {
        ...o.scales.yAxes[0].ticks,
        beginAtZero: true,
        precision: 0,
        fontColor: '#6b6478',
      }
      delete o.scales.yAxes[0].ticks.stepSize
      return o
    },
    chartOptionsFunilMini() {
      return {
        responsive: true,
        maintainAspectRatio: false,
        legend: { display: false },
        tooltips: { enabled: false },
        scales: {
          xAxes: [{ display: false }],
          yAxes: [{ display: false, ticks: { beginAtZero: true } }],
        },
        elements: { point: { radius: 0 } },
      }
    },
    chartOptionsTransactionalHero() {
      return {
        responsive: true,
        maintainAspectRatio: false,
        legend: { display: false },
        tooltips: {
          mode: 'index',
          intersect: false,
          backgroundColor: 'rgba(255,255,255,0.96)',
          titleFontColor: '#1e1b2e',
          bodyFontColor: '#4a4458',
        },
        scales: {
          xAxes: [{
            gridLines: { display: false, color: 'transparent' },
            ticks: { fontColor: 'rgba(255,255,255,0.55)', maxTicksLimit: 8 },
          }],
          yAxes: [{
            gridLines: { color: 'rgba(255,255,255,0.08)', zeroLineColor: 'transparent' },
            ticks: { beginAtZero: true, fontColor: 'rgba(255,255,255,0.45)' },
          }],
        },
      }
    },
    vendasIncentivadasCount() {
      const aprovados = (this.report || []).filter(r => r.status_id === 1)
      if (!aprovados.length) return 0
      const oneDayMs = 1 * 24 * 60 * 60 * 1000
      const sevenDaysMs = 7 * 24 * 60 * 60 * 1000
      const carrinhos = this.getConversionsInWindow(this.reportCarts || [], aprovados, oneDayMs, sevenDaysMs)
      const cancelados = this.getConversionsInWindow((this.report || []).filter(r => r.status_id === 3), aprovados, oneDayMs, sevenDaysMs)
      return carrinhos + cancelados
    },
    taxaConversaoCarrinhos() {
      const carrinhos = this.reportCarts.length
      if (!carrinhos) return '-'
      const v = this.vendasIncentivadasCount
      return `${((v / carrinhos) * 100).toFixed(1)}% (${v}/${carrinhos})`
    },
    pedidosUnicos() {
      const ids = new Set()
      this.report.forEach(r => {
        if (r.status_id === 8) return
        if (r.order_id != null) ids.add(r.order_id)
      })
      return ids.size
    },
    mediaNotificacoesPorDia() {
      const total = this.report.length + this.reportCarts.length + (this.cashback || []).length
      if (!total || !this.rangeDate) return '-'
      const parts = (this.rangeDate || '').replace(/\s/g, '').split('to')
      const start = parts[0] ? new Date(parts[0]) : null
      const end = parts[1] ? new Date(parts[1]) : (start ? new Date(start) : null)
      if (!start || !end) return total
      const days = Math.max(1, Math.ceil((end - start) / (24 * 60 * 60 * 1000)))
      return (total / days).toFixed(1)
    },
  },
  watch: {
    trendStatusFilter: {
      handler() {
        this.$nextTick(() => this.buildChartTrend())
      },
      deep: true,
    },
    filterTipoUnified() {
      this.currentPageUnified = 1
    },
    filterBuscaUnified() {
      this.currentPageUnified = 1
    },
    dataTableUnified() {
      this.$nextTick(() => {
        if (this.filterTipoUnified === 'all') return
        const vals = new Set((this.dataTableUnified || []).map(r => String(r.tipo || '')))
        if (!vals.has(this.filterTipoUnified)) {
          this.filterTipoUnified = 'all'
        }
      })
    },
  },
  async mounted() {
    // Configurar locale pt-BR do flatpickr
    this.$flatpickr = await import('flatpickr')
    this.$flatpickr.default.localize(Portuguese)

    this.stores = await stores()
    const list = this.stores || []
    if (list.length > 0) {
      this.store = list[0]
    }
    const end = new Date()
    const start = new Date()
    start.setDate(start.getDate() - 6)
    const formatDate = d => {
      const day = String(d.getDate()).padStart(2, '0')
      const month = String(d.getMonth() + 1).padStart(2, '0')
      const year = d.getFullYear()
      return `${day}-${month}-${year}`
    }
    this.rangeDate = `${formatDate(start)} até ${formatDate(end)}`
    if (this.store && this.rangeDate) {
      await this.getData()
    }
  },
  methods: {
    onKpiCardClick(kpi) {
      if (!kpi || kpi.blurOfficialOnly) return
      if (kpi.detailAction === 'orders-cart-cancel') {
        this.openCartCancelOrdersDetailModal()
        return
      }
      if (!kpi.expandable) return
      this.kpiReceitaExpanded = !this.kpiReceitaExpanded
    },
    /** d-m-Y (flat-pickr) → YYYY-MM-DD para params da API de relatório. */
    dmyToYmdApi(dmy) {
      const datePart = String(dmy || '').trim().split(/\s+/)[0]
      const segs = datePart.split('-')
      if (segs.length !== 3) return datePart
      const [day, month, year] = segs
      if (!year || String(year).length !== 4) return datePart
      return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`
    },
    parseDate(value) {
      if (value == null || value === '') return null
      if (value instanceof Date) return value.getTime()
      if (typeof value === 'number') return Number.isNaN(value) ? null : value
      if (typeof value !== 'string') return null
      // ISO
      if (value.indexOf('T') !== -1) return new Date(value).getTime()
      // data_formatted "2026/02/02 14:32" ou "2026/02/02"
      const parts = String(value).replace(/\//g, '-').split(' ')
      const d = parts[0]
      const t = parts[1] || '00:00'
      const parsed = new Date(`${d}T${t}:00`).getTime()
      return Number.isNaN(parsed) ? null : parsed
    },
    /** Vendas incentivadas = carrinhos com pedido aprovado entre 1 e 7 dias (exclui os que viraram pago em até 1 dia). */
    getVendasIncentivadas(reportCarts, report) {
      if (!reportCarts || !reportCarts.length || !report || !report.length) return 0
      const aprovados = report.filter(r => r.status_id === 1)
      if (!aprovados.length) return 0
      const oneDayMs = 1 * 24 * 60 * 60 * 1000
      const sevenDaysMs = 7 * 24 * 60 * 60 * 1000
      return this.getConversionsInWindow(reportCarts, aprovados, oneDayMs, sevenDaysMs)
    },
    getTable(historyNotifications) {
      const notifications = []
      historyNotifications.forEach(notification => {
        const key = `${notification.order_id}-${notification.status_id}`
        if (!notifications.find(n => n.order_id === notification.order_id && n.status_id === STATUS_LIST[notification.status_id])) {
          notifications.push({
            status_id: STATUS_LIST[notification.status_id],
            created_at: notification.data_formatted,
            name: notification.name,
            phone: notification.phone,
            send: notification.message_id ? 1 : 2,
            order_id: notification.order_id,
            platform_id: notification.platform_id,
          })
        }
      })
      return notifications
    },
    getPaymentPanding(transactions) {
      if (!Array.isArray(transactions)) return
      const methodPayment = { 2: [], 6: [] }
      transactions.forEach(transaction => {
        const pid = transaction.payment_id
        if (transaction.status_id === 2 && (pid === 2 || pid === 6)) {
          const arr = methodPayment[pid]
          if (arr && !arr.includes(transaction.order_id)) {
            arr.push(transaction.order_id)
          }
        }
      })
      this.paymentData[0].qtd = (methodPayment[6] && methodPayment[6].length) || 0
      this.paymentData[1].qtd = (methodPayment[2] && methodPayment[2].length) || 0
    },
    getResume(historyNotifications) {
      if (!Array.isArray(historyNotifications)) return
      const cust = Array.isArray(this.customers) ? this.customers : []
      historyNotifications.forEach(notification => {
        if (notification.customer_id != null && !cust.includes(notification.customer_id)) {
          cust.push(notification.customer_id)
        }
      })
      this.customers = cust
    },
    getReport(historyNotifications) {
      if (!Array.isArray(historyNotifications)) return
      const customers = {}
      const uniqueKey = (n, statusId) => {
        if (n.customer_id != null) return `c_${n.customer_id}`
        if (statusId === 11 && (n.phone != null && n.phone !== '')) return `p_${n.phone}`
        if (n.id != null) return `id_${n.id}`
        return null
      }
      historyNotifications.forEach(notification => {
        const statusId = notification.status_id
        const findStatus = this.dataReport.find(el => el.statusId === statusId)
        const key = uniqueKey(notification, statusId)
        if (!findStatus) {
          customers[statusId] = key != null ? [key] : []
          this.dataReport.push({
            statusId,
            status: statusId,
            enviados: 1,
            contactados: customers[statusId].length,
            pagos: '-',
            pagosAmount: '-',
            incentivadas: '-',
            incentivadasAmount: '-',
          })
        } else {
          if (!customers[statusId]) customers[statusId] = []
          if (key != null && !customers[statusId].includes(key)) {
            customers[statusId].push(key)
          }
          findStatus.enviados += 1
          findStatus.contactados = customers[statusId].length
        }
      })
    },
    /** Pedidos únicos em aguardando pagamento (status 2): contagem e soma de amount no relatório bruto. */
    computeAguardandoPagamentoAggregates() {
      const seen = new Set()
      let sum = 0;
      (this.report || []).forEach(r => {
        if (r.status_id !== 2 || r.order_id == null) return
        const oid = r.order_id
        if (seen.has(oid)) return
        seen.add(oid)
        if (r.amount !== undefined && r.amount !== null) {
          const amt = Number(r.amount)
          if (!Number.isNaN(amt)) sum += amt
        }
      })
      return { uniqueCount: seen.size, totalAmount: sum }
    },
    getPagosRecuperados(listaNotificacoes, reportAprovados, maxDaysMs) {
      return this.collectUniqueApprovedOrdersForWindow(listaNotificacoes, reportAprovados, 0, maxDaysMs).length
    },
    /** Retorna { count, totalAmount } para notificações que tiveram aprovação em até maxDaysMs. totalAmount = soma dos amount dos pedidos aprovados únicos (por platform_id/order_id). */
    getPagosRecuperadosAmount(listaNotificacoes, reportAprovados, maxDaysMs) {
      const items = this.collectUniqueApprovedOrdersForWindow(listaNotificacoes, reportAprovados, 0, maxDaysMs)
      return {
        count: items.length,
        totalAmount: items.reduce((sum, item) => sum + (item.amountValue || 0), 0),
      }
    },
    /** Conta notificações que tiveram aprovação em uma janela [minMs, maxMs] após a notificação (ex.: 1 a 7 dias = incentivadas, excluindo 0–1 dia = pagos). */
    getConversionsInWindow(listaNotificacoes, reportAprovados, minMs, maxMs) {
      return this.collectUniqueApprovedOrdersForWindow(listaNotificacoes, reportAprovados, minMs, maxMs).length
    },
    /** Retorna { count, totalAmount } para a janela [minMs, maxMs]. totalAmount = soma dos amount dos pedidos aprovados únicos. */
    getConversionsInWindowAmount(listaNotificacoes, reportAprovados, minMs, maxMs) {
      const items = this.collectUniqueApprovedOrdersForWindow(listaNotificacoes, reportAprovados, minMs, maxMs)
      return {
        count: items.length,
        totalAmount: items.reduce((sum, item) => sum + (item.amountValue || 0), 0),
      }
    },
    getOrderReference(item) {
      if ((item && item.platform_id) != null && item.platform_id !== '') return item.platform_id
      if ((item && item.order_id) != null && item.order_id !== '') return item.order_id
      return '-'
    },
    getOrderDedupKey(item) {
      if ((item && item.platform_id) != null && item.platform_id !== '') return `platform:${item.platform_id}`
      if ((item && item.order_id) != null && item.order_id !== '') return `order:${item.order_id}`
      if ((item && item.id) != null && item.id !== '') return `id:${item.id}`
      const customerKey = (item && item.customer_id) != null ? item.customer_id : 'unknown'
      const dateKey = item && (item.created_at || item.data_formatted) ? String(item.created_at || item.data_formatted) : 'unknown'
      const amountKey = item && item.amount != null ? String(item.amount) : 'unknown'
      return `fallback:${customerKey}:${dateKey}:${amountKey}`
    },
    mergeOrderOrigins(currentOrigin, nextOrigin) {
      if (!currentOrigin) return nextOrigin || ''
      if (!nextOrigin || currentOrigin === nextOrigin) return currentOrigin
      const origins = new Set(String(currentOrigin).split(' | ').filter(Boolean))
      origins.add(nextOrigin)
      return Array.from(origins).join(' | ')
    },
    buildStatusConversionItems(statusId) {
      const aprovados = (this.report || []).filter(r => r.status_id === 1)
      if (!aprovados.length) return []
      const oneDayMs = 1 * 24 * 60 * 60 * 1000
      const sevenDaysMs = 7 * 24 * 60 * 60 * 1000
      if (statusId === 8) {
        return [
          ...this.collectApprovedOrdersForWindow(this.reportCarts || [], aprovados, 0, oneDayMs, 'Carrinho abandonado • direta (24h)'),
          ...this.collectApprovedOrdersForWindow(this.reportCarts || [], aprovados, oneDayMs, sevenDaysMs, 'Carrinho abandonado • janela de 7 dias'),
        ]
      }
      if (statusId === 3) {
        const cancelados = (this.report || []).filter(r => r.status_id === 3)
        return [
          ...this.collectApprovedOrdersForWindow(cancelados, aprovados, 0, oneDayMs, 'Cancelado • direta (24h)'),
          ...this.collectApprovedOrdersForWindow(cancelados, aprovados, oneDayMs, sevenDaysMs, 'Cancelado • janela de 7 dias'),
        ]
      }
      return []
    },
    buildCartCancelConversionItems() {
      return [
        ...this.buildStatusConversionItems(8),
        ...this.buildStatusConversionItems(3),
      ]
    },
    collectUniqueApprovedOrdersForWindow(listaNotificacoes, reportAprovados, minMs, maxMs, origem = '') {
      if (!Array.isArray(listaNotificacoes) || !listaNotificacoes.length || !Array.isArray(reportAprovados) || !reportAprovados.length) {
        return []
      }
      const uniqueOrders = new Map()
      listaNotificacoes.forEach(notif => {
        const notifTime = this.parseDate(notif.created_at || notif.data_formatted)
        if (!notifTime || notif.customer_id == null) return
        const inicioJanela = notifTime + minMs
        const fimJanela = notifTime + maxMs
        for (let i = 0; i < reportAprovados.length; i += 1) {
          const aprovado = reportAprovados[i]
          const aprovadoTime = this.parseDate(aprovado.created_at || aprovado.data_formatted)
          const matchesWindowStart = minMs > 0 ? aprovadoTime > inicioJanela : aprovadoTime >= inicioJanela
          if (
            aprovadoTime
            && aprovado.customer_id === notif.customer_id
            && matchesWindowStart
            && aprovadoTime <= fimJanela
          ) {
            const rawAmount = aprovado && aprovado.amount
            const parsedAmount = rawAmount !== undefined && rawAmount !== null ? Number(rawAmount) : null
            const amountValue = parsedAmount != null && !Number.isNaN(parsedAmount) ? parsedAmount : 0
            const dedupKey = this.getOrderDedupKey(aprovado)
            const nextItem = {
              data_formatted: (aprovado && (aprovado.data_formatted || aprovado.created_at)) || '-',
              name: (aprovado && aprovado.name) || `Cliente ${(aprovado && aprovado.customer_id) != null ? aprovado.customer_id : '-'}`,
              phone: (aprovado && aprovado.phone) || '-',
              order_id: this.getOrderReference(aprovado),
              amount: rawAmount !== undefined && rawAmount !== null ? this.formatBRL(amountValue) : '-',
              amountValue,
              origem,
              sortTs: aprovadoTime,
              dedupKey,
            }
            const existingItem = uniqueOrders.get(dedupKey)
            if (!existingItem) {
              uniqueOrders.set(dedupKey, nextItem)
            } else {
              existingItem.origem = this.mergeOrderOrigins(existingItem.origem, origem)
              if ((nextItem.sortTs || 0) > (existingItem.sortTs || 0)) {
                existingItem.data_formatted = nextItem.data_formatted
                existingItem.name = nextItem.name
                existingItem.phone = nextItem.phone
                existingItem.order_id = nextItem.order_id
                existingItem.amount = nextItem.amount
                existingItem.amountValue = nextItem.amountValue
                existingItem.sortTs = nextItem.sortTs
              }
            }
            break
          }
        }
      })
      return Array.from(uniqueOrders.values())
    },
    collectApprovedOrdersForWindow(listaNotificacoes, reportAprovados, minMs, maxMs, origem) {
      return this.collectUniqueApprovedOrdersForWindow(listaNotificacoes, reportAprovados, minMs, maxMs, origem)
    },
    normalizeCountedOrders(items, preserveMeta = false) {
      const uniqueOrders = new Map();
      (items || []).forEach(item => {
        const dedupKey = item && item.dedupKey ? item.dedupKey : `${item.order_id || '-'}|${item.data_formatted || '-'}`
        const existingItem = uniqueOrders.get(dedupKey)
        if (!existingItem) {
          uniqueOrders.set(dedupKey, { ...item, dedupKey })
          return
        }
        existingItem.origem = this.mergeOrderOrigins(existingItem.origem, item.origem)
        if ((item.sortTs || 0) > (existingItem.sortTs || 0)) {
          existingItem.data_formatted = item.data_formatted
          existingItem.name = item.name
          existingItem.phone = item.phone
          existingItem.order_id = item.order_id
          existingItem.amount = item.amount
          existingItem.amountValue = item.amountValue
          existingItem.sortTs = item.sortTs
        }
      })
      const normalized = Array.from(uniqueOrders.values())
        .sort((a, b) => (b.sortTs || 0) - (a.sortTs || 0))
      if (preserveMeta) return normalized
      return normalized.map(({
        sortTs, dedupKey, amountValue, ...item
      }) => item)
    },
    getUniqueStatusConversionSummary(statusId) {
      if (statusId !== 3 && statusId !== 8) return null
      const items = this.normalizeCountedOrders(this.buildStatusConversionItems(statusId), true)
      return {
        count: items.length,
        totalAmount: items.reduce((sum, item) => sum + (item.amountValue || 0), 0),
      }
    },
    getUniqueCartCancelRevenueSummary() {
      const items = this.normalizeCountedOrders(this.buildCartCancelConversionItems(), true)
      return items.reduce((summary, item) => {
        const amount = item.amountValue || 0
        const origem = String(item.origem || '')
        summary.totalAmount += amount
        if (origem.includes('direta (24h)')) {
          summary.directAmount += amount
        } else if (origem.includes('janela de 7 dias')) {
          summary.influencedAmount += amount
        }
        return summary
      }, {
        directAmount: 0,
        influencedAmount: 0,
        totalAmount: 0,
      })
    },
    getApprovedOrdersCountedInCartCompras() {
      return this.normalizeCountedOrders(this.buildStatusConversionItems(8))
    },
    getApprovedOrdersCountedInCartCancelCards() {
      return this.normalizeCountedOrders(this.buildCartCancelConversionItems())
    },
    openOrderCountDetailModal(title, items) {
      this.selectedSummaryStatusId = null
      this.modalDetailType = 'orders'
      this.modalDetailStatusLabel = title
      this.modalDetailItems = items
      this.$bvModal.show('modal-detail-status')
    },
    openCartComprasDetailModal() {
      this.openOrderCountDetailModal('Compras', this.getApprovedOrdersCountedInCartCompras())
    },
    openCartCancelOrdersDetailModal() {
      this.openOrderCountDetailModal('Pedidos gerados', this.getApprovedOrdersCountedInCartCancelCards())
    },
    formatBRL(value) {
      if (value == null || value === '' || Number.isNaN(Number(value))) return '-'
      return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(Number(value))
    },
    /** Cliques no retorno Meta: número ou lista { count } */
    normalizeMetaClicked(raw) {
      if (raw == null) return 0
      if (typeof raw === 'number' && !Number.isNaN(raw)) return raw
      if (Array.isArray(raw)) {
        return raw.reduce((s, item) => s + (Number(item && item.count) || 0), 0)
      }
      return 0
    },
    formatDmyDisplay(str) {
      const datePart = String(str || '').trim().split(/\s+/)[0]
      const segs = datePart.split('-')
      if (segs.length !== 3) return ''
      const [d, m, y] = segs
      if (!y || String(y).length !== 4) return ''
      return `${String(d).padStart(2, '0')}/${String(m).padStart(2, '0')}/${y}`
    },
    getReportRangeDaySpan() {
      if (!this.rangeDate) return null
      const raw = String(this.rangeDate).trim().replace(/\s+/g, ' ')
      const lower = raw.toLowerCase()
      const sep = lower.includes('até') ? /\s+até\s+/i : (lower.includes(' a ') ? /\s+a\s+/i : null)
      if (!sep) return null
      const parts = raw.split(sep).map(s => s.trim()).filter(Boolean)
      if (parts.length < 2) return null
      const t1 = this.parseDmyToTimestamp(parts[0])
      const t2 = this.parseDmyToTimestamp(parts[parts.length - 1])
      if (t1 == null || t2 == null) return null
      return Math.round(Math.abs(t2 - t1) / 86400000) + 1
    },
    parseDmyToTimestamp(str) {
      const datePart = String(str || '').trim().split(/\s+/)[0]
      const segs = datePart.split('-')
      if (segs.length !== 3) return null
      const d = parseInt(segs[0], 10)
      const mo = parseInt(segs[1], 10)
      const y = parseInt(segs[2], 10)
      if (!y || y < 2000 || Number.isNaN(d) || Number.isNaN(mo)) return null
      const t = new Date(y, mo - 1, d, 12, 0, 0, 0).getTime()
      return Number.isNaN(t) ? null : t
    },
    formatNumberBR(n) {
      if (n == null || Number.isNaN(Number(n))) return '0'
      return new Intl.NumberFormat('pt-BR').format(Number(n))
    },
    num(v) {
      if (v === '-' || v == null || v === '') return 0
      const n = Number(v)
      return Number.isNaN(n) ? 0 : n
    },
    parseBrlToNumber(str) {
      if (str == null || str === '' || str === '-') return 0
      const cleaned = String(str).trim().replace(/[R$\s]/g, '')
      if (!cleaned) return 0
      const normalized = cleaned.includes(',')
        ? cleaned.replace(/\./g, '').replace(',', '.')
        : cleaned
      const n = parseFloat(normalized)
      return Number.isNaN(n) ? 0 : n
    },
    shortStatusLabel(statusId, statusFull) {
      const names = {
        1: 'Aprovado',
        2: 'Aguardando pagamento',
        3: 'Cancelado',
        4: 'Transporte',
        5: 'Entregue',
        6: 'Boleto vencido',
        7: 'Aprov. parcial',
        8: 'Carrinho abandonado',
        9: 'Aprov. integrado',
        10: 'Transp. interno',
        11: 'Cashback',
      }
      if (names[statusId]) return names[statusId]
      const fb = (statusFull || STATUS_LIST[statusId] || '').trim()
      return fb || `Status ${statusId}`
    },
    statusDotClass(statusId) {
      const m = {
        1: 'dn-dot--green',
        2: 'dn-dot--orange',
        3: 'dn-dot--red',
        4: 'dn-dot--blue',
        5: 'dn-dot--teal',
        6: 'dn-dot--orange',
        7: 'dn-dot--slate',
        8: 'dn-dot--purple',
        9: 'dn-dot--green',
        10: 'dn-dot--teal',
        11: 'dn-dot--teal',
      }
      return m[statusId] || 'dn-dot--slate'
    },
    fillPagosAndIncentivadasByStatus() {
      const aprovados = (this.report || []).filter(r => r.status_id === 1)
      const oneDayMs = 1 * 24 * 60 * 60 * 1000
      const sevenDaysMs = 7 * 24 * 60 * 60 * 1000
      this.dataReport.forEach(row => {
        if (row.statusId === 3) {
          const lista3 = this.report.filter(r => r.status_id === 3)
          const pagosRes = this.getPagosRecuperadosAmount(lista3, aprovados, oneDayMs)
          const incRes = this.getConversionsInWindowAmount(lista3, aprovados, oneDayMs, sevenDaysMs)
          row.pagos = pagosRes.count
          row.pagosAmount = pagosRes.totalAmount > 0 ? this.formatBRL(pagosRes.totalAmount) : '-'
          row.incentivadas = incRes.count
          row.incentivadasAmount = incRes.totalAmount > 0 ? this.formatBRL(incRes.totalAmount) : '-'
        } else if (row.statusId === 8) {
          const lista8 = this.reportCarts || []
          const pagosRes = this.getPagosRecuperadosAmount(lista8, aprovados, oneDayMs)
          const incRes = this.getConversionsInWindowAmount(lista8, aprovados, oneDayMs, sevenDaysMs)
          row.pagos = pagosRes.count
          row.pagosAmount = pagosRes.totalAmount > 0 ? this.formatBRL(pagosRes.totalAmount) : '-'
          row.incentivadas = incRes.count
          row.incentivadasAmount = incRes.totalAmount > 0 ? this.formatBRL(incRes.totalAmount) : '-'
        }
      })
    },
    buildChartTransactionalHero() {
      const dates = new Set()
      this.report.forEach(r => {
        const k = this.getDateKey(r)
        if (k) dates.add(k)
      })
      this.reportCarts.forEach(c => {
        const k = this.getDateKey(c)
        if (k) dates.add(k)
      });
      (this.cashback || []).forEach(cb => {
        const k = this.getDateKey(cb)
        if (k) dates.add(k)
      })
      const sortedDates = Array.from(dates).filter(Boolean).sort()
      const labels = sortedDates.map(d => {
        const p = d.split('-')
        return p.length === 3 ? `${p[2]}/${p[1]}` : d
      })
      const totalPerDay = sortedDates.map(d => this.report.filter(r => this.getDateKey(r) === d).length
        + this.reportCarts.filter(c => this.getDateKey(c) === d).length
        + (this.cashback || []).filter(cb => this.getDateKey(cb) === d).length)
      const cartsPerDay = sortedDates.map(d => this.reportCarts.filter(c => this.getDateKey(c) === d).length)
      if (!sortedDates.length) {
        this.chartTransactionalHero = { labels: [], datasets: [] }
        return
      }
      this.chartTransactionalHero = {
        labels,
        datasets: [
          {
            label: 'Envios',
            data: totalPerDay,
            borderColor: 'rgba(255,255,255,0.92)',
            backgroundColor: 'rgba(196, 181, 253, 0.4)',
            fill: true,
            lineTension: 0.35,
            pointRadius: 0,
          },
          {
            label: 'Carrinhos',
            data: cartsPerDay,
            borderColor: 'rgba(125, 211, 252, 0.9)',
            backgroundColor: 'rgba(56, 189, 248, 0.18)',
            fill: true,
            lineTension: 0.35,
            pointRadius: 0,
          },
        ],
      }
    },
    buildChartFunilMini() {
      const dates = new Set()
      this.reportCarts.forEach(c => {
        const k = this.getDateKey(c)
        if (k) dates.add(k)
      })
      const sorted = Array.from(dates).filter(Boolean).sort()
      const tail = sorted.length ? sorted.slice(-12) : []
      const data = tail.length
        ? tail.map(d => this.reportCarts.filter(c => this.getDateKey(c) === d).length)
        : [2, 3, 2, 5, 4, 6, 5, 7]
      const labels = data.map((_, i) => String(i))
      this.chartFunilMini = {
        labels,
        datasets: [{
          label: '',
          data,
          borderColor: '#7c3aed',
          backgroundColor: 'rgba(124, 58, 237, 0.14)',
          fill: true,
          lineTension: 0.4,
          pointRadius: 0,
        }],
      }
    },
    getDateKey(item) {
      const raw = item.data_formatted || item.created_at || ''
      if (!raw) return ''
      const t = this.parseDate(raw)
      if (!t) return ''
      const d = new Date(t)
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
    },
    getNotificationsForStatus(statusId) {
      if (statusId === 8) {
        return (this.reportCarts || []).map(c => ({
          data_formatted: (c && c.data_formatted) || '-',
          name: (c && c.name) || `Cliente ${(c && c.customer_id) != null ? c.customer_id : '-'}`,
          phone: (c && c.phone) || '-',
          order_id: (c && c.abandoned_cart_id) != null ? `Carrinho #${c.abandoned_cart_id}` : '-',
        }))
      }
      if (statusId === 11) {
        return (this.cashback || []).map(cb => ({
          data_formatted: (cb && cb.data_formatted) || '-',
          name: (cb && cb.name) || '-',
          phone: (cb && cb.phone) || '-',
          order_id: (cb && cb.cashback_id) != null ? `Cashback #${cb.cashback_id}` : '-',
        }))
      }
      return (this.report || [])
        .filter(r => r && r.status_id === statusId)
        .map(r => ({
          data_formatted: (r && r.data_formatted) || '-',
          name: (r && r.name) || `Cliente ${(r && r.customer_id) != null ? r.customer_id : '-'}`,
          phone: (r && r.phone) || '-',
          order_id: this.getOrderReference(r),
        }))
    },
    onSummaryRowClick(row) {
      this.selectedSummaryStatusId = row.statusId
      this.modalDetailType = 'status'
      const full = (this.dataReport || []).find(r => r.statusId === row.statusId)
      this.modalDetailStatusLabel = full ? full.status : row.labelShort
      this.modalDetailItems = this.getNotificationsForStatus(row.statusId)
      this.$bvModal.show('modal-detail-status')
    },
    isTrendFilterActive(val) {
      return (this.trendStatusFilter || []).includes(val)
    },
    toggleTrendFilter(val) {
      const cur = [...(this.trendStatusFilter || [])]
      const idx = cur.indexOf(val)
      if (idx >= 0) {
        if (cur.length <= 1) return
        cur.splice(idx, 1)
      } else {
        cur.push(val)
      }
      this.trendStatusFilter = cur
    },
    trendChipCssVars(val) {
      if (!this.isTrendFilterActive(val)) {
        return {
          '--trend-accent': 'rgba(156, 151, 173, 0.9)',
          '--trend-accent-soft': 'transparent',
        }
      }
      const st = trendStyleForFilterValue(val)
      return {
        '--trend-accent': st.line,
        '--trend-accent-soft': st.area,
      }
    },
    formatTrendAxisLabel(ymd) {
      if (!ymd || typeof ymd !== 'string') return ''
      const p = ymd.split('-')
      if (p.length !== 3) return ymd
      return `${p[2]}/${p[1]}`
    },
    formatTrendTooltipTitle(ymd) {
      if (!ymd || typeof ymd !== 'string') return ''
      const p = ymd.split('-')
      if (p.length !== 3) return ymd
      return `${p[2]}/${p[1]}/${p[0]}`
    },
    buildChartTrend() {
      const dates = new Set()
      this.report.forEach(r => {
        const key = this.getDateKey(r)
        if (key) dates.add(key)
      })
      this.reportCarts.forEach(c => {
        const key = this.getDateKey(c)
        if (key) dates.add(key)
      });
      (this.cashback || []).forEach(cb => {
        const key = this.getDateKey(cb)
        if (key) dates.add(key)
      })
      const sortedDates = Array.from(dates).filter(Boolean).sort()
      this.chartTrend.labels = sortedDates
      const selected = this.trendStatusFilter || []
      const datasets = []
      const pushTrendDataset = (filterKey, label, dataPoints) => {
        const st = trendStyleForFilterValue(filterKey)
        datasets.push({
          label,
          data: dataPoints,
          borderColor: st.line,
          backgroundColor: st.area,
          fill: true,
          lineTension: 0.4,
          borderWidth: 2,
          pointRadius: 0,
          pointHoverRadius: 5,
          pointHoverBorderWidth: 2,
          pointBackgroundColor: st.line,
          pointBorderColor: '#ffffff',
        })
      }
      if (selected.includes('all_trans')) {
        pushTrendDataset(
          'all_trans',
          'Transacionais (todos)',
          sortedDates.map(d => this.report.filter(r => this.getDateKey(r) === d).length),
        )
      }
      if (selected.includes(8)) {
        pushTrendDataset(
          8,
          'Carrinhos abandonados',
          sortedDates.map(d => this.reportCarts.filter(c => this.getDateKey(c) === d).length),
        )
      }
      if (selected.includes(11)) {
        pushTrendDataset(
          11,
          'Cashback',
          sortedDates.map(d => (this.cashback || []).filter(cb => this.getDateKey(cb) === d).length),
        )
      }
      [1, 2, 3, 4, 5, 6, 7, 9, 10].forEach(sid => {
        if (selected.includes(sid)) {
          const label = STATUS_LIST[sid] || `Status ${sid}`
          pushTrendDataset(
            sid,
            label,
            sortedDates.map(d => this.report.filter(r => r.status_id === sid && this.getDateKey(r) === d).length),
          )
        }
      })
      this.chartTrend.datasets = datasets
    },
    async getData() {
      if (!this.store || !this.rangeDate) {
        this.showDismissibleAlert = true
        return
      }
      const rangeSpan = this.getReportRangeDaySpan()
      if (rangeSpan != null && rangeSpan > REPORT_MAX_RANGE_DAYS) {
        this.$bvToast.toast(`Selecione um período de no máximo ${REPORT_MAX_RANGE_DAYS} dias.`, {
          title: 'Período inválido',
          variant: 'warning',
          solid: true,
          autoHideDelay: 5000,
        })
        return
      }
      this.showDismissibleAlert = false
      this.buttonDisabled = true
      this.hasSearched = true

      this.rawReport = null
      this.templateAnalytics = null
      this.report = []
      this.reportCarts = []
      this.cashback = []
      this.dataReport = []
      this.dataTable = []
      this.dataTableCarts = []
      this.dataTableCashback = []
      this.customers = []
      this.paymentData[0].qtd = 0
      this.paymentData[1].qtd = 0
      this.chartTransactionalHero = { labels: [], datasets: [] }
      this.chartFunilMini = { labels: [], datasets: [] }
      this.selectedSummaryStatusId = null
      this.kpiReceitaExpanded = false
      this.chartTrend.labels = []
      this.chartTrend.datasets = []

      const dataRange = this.rangeDate.replace(/\s/g, '').split('até')
      const dataMin = this.dmyToYmdApi(dataRange[0])
      const dataMax = this.dmyToYmdApi(dataRange[1] || dataRange[0])

      try {
        const [dataReport, analyticsRaw] = await Promise.all([
          report(dataMin, dataMax, this.store.uuid),
          fetchTemplateAnalytics(this.store.uuid, dataMin, dataMax).catch(err => {
            console.warn('Analytics de templates (Meta):', err)
            return null
          }),
        ])
        this.templateAnalytics = analyticsRaw && analyticsRaw.sucesso && !analyticsRaw.sem_metricas
          ? analyticsRaw
          : null
        this.rawReport = dataReport
        const reportList = dataReport.report || []
        const reportCartsList = dataReport.reportCarts || []
        const cashbackList = dataReport.cashback || []

        this.getResume(reportCartsList)
        this.getResume(reportList)
        this.getResume(cashbackList)
        this.getReport(reportCartsList)
        this.getReport(reportList)
        this.getReport(cashbackList)
        this.report = reportList
        this.reportCarts = reportCartsList
        this.cashback = cashbackList
        this.fillPagosAndIncentivadasByStatus()
        this.dataReport.forEach(el => {
          el.status = STATUS_LIST[el.statusId] || el.status
        })
        this.dataTable = this.getTable(reportList)
        this.dataTableCarts = reportCartsList.map(c => ({
          data_formatted: (c && c.data_formatted) || '-',
          abandoned_cart_id: (c && c.abandoned_cart_id) != null ? c.abandoned_cart_id : '-',
          name: (c && c.name) || '-',
          phone: (c && c.phone) || '-',
          envio: (c && c.message_id) ? 'Enviado' : '-',
        }))
        this.dataTableCashback = cashbackList.map(cb => ({
          data_formatted: (cb && cb.data_formatted) || '-',
          name: (cb && cb.name) || '-',
          phone: (cb && cb.phone) || '-',
          envio: (cb && cb.message_id) ? 'Enviado' : '-',
        }))
        this.currentPageUnified = 1
        this.getPaymentPanding(reportList)
        this.$nextTick(() => {
          const opts = this.trendStatusOptions
          const validValues = opts.map(o => o.value)
          this.trendStatusFilter = (this.trendStatusFilter || []).filter(v => validValues.includes(v))
          if (this.trendStatusFilter.length === 0 && opts.length > 0) {
            this.trendStatusFilter = opts.length >= 2
              ? [opts[0].value, opts[1].value]
              : [opts[0].value]
          }
          this.buildChartTrend()
          this.buildChartTransactionalHero()
          this.buildChartFunilMini()
        })
      } catch (err) {
        console.error('Erro ao carregar relatório:', err)
        this.templateAnalytics = null
        this.$bvToast.toast('Erro ao carregar relatório. Tente novamente.', {
          title: 'Erro',
          variant: 'danger',
          solid: true,
        })
      } finally {
        this.buttonDisabled = false
      }
    },
  },
}
</script>

<style lang="scss" scoped>
/* ManyConvert / dashboard notificações — visual premium (referência analítica) */
$dn-lilac-0: #faf8ff;
$dn-lilac-1: #f3efff;
$dn-lilac-2: #e8e0ff;
$dn-ink: #1a1523;
$dn-muted: #6b6478;
$dn-line: rgba(107, 100, 120, 0.12);
$dn-shadow: 0 10px 40px rgba(91, 33, 182, 0.07);
$dn-radius: 20px;
$dn-radius-sm: 14px;

.dn-page.dashboard-notifications {
  min-height: 100%;
  padding: 1.75rem 1.5rem 2.5rem;
  background:
    radial-gradient(ellipse 85% 140% at 50% -35%, rgba(196, 181, 253, 0.38) 0%, transparent 55%),
    linear-gradient(165deg, #fdfcff 0%, $dn-lilac-1 38%, #f6f3ff 72%, #faf7ff 100%);
  border-radius: 0;
}

/* Header alinhado ao hero de desempenho-atendentes (ManyConvert) */
.dn-hero {
  --dn-hero-gradient: linear-gradient(135deg, #6f35d0 0%, #b13ccf 45%, #ff6f61 100%);
  --dn-hero-shadow: 0 18px 45px rgba(120, 70, 180, 0.18);
  border-radius: 28px;
  background: var(--dn-hero-gradient);
  box-shadow: var(--dn-hero-shadow);
  padding: 36px 40px;
  margin-bottom: 28px;
  font-family: 'Plus Jakarta Sans', 'Sora', system-ui, sans-serif;
}

.dn-hero__inner {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1.5rem;
}

.dn-hero__left {
  display: flex;
  align-items: flex-start;
  gap: 1.25rem;
  flex: 1;
  min-width: 0;
}

.dn-hero__icon-wrap {
  flex-shrink: 0;
  width: 56px;
  height: 56px;
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.22);
  border: 1px solid rgba(255, 255, 255, 0.35);
  display: flex;
  align-items: center;
  justify-content: center;
}

.dn-hero__icon {
  color: #fff !important;
}

.dn-hero__title {
  margin: 0 0 0.5rem;
  font-size: clamp(1.65rem, 4vw, 2.35rem);
  font-weight: 800;
  color: #fff;
  letter-spacing: -0.03em;
  line-height: 1.15;
}

.dn-hero__subtitle {
  margin: 0 0 0.75rem;
  font-size: clamp(0.95rem, 2vw, 1.1rem);
  font-weight: 500;
  color: rgba(255, 255, 255, 0.92);
  line-height: 1.5;
  max-width: 520px;
}

.dn-hero__home {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  color: rgba(255, 255, 255, 0.95) !important;
  font-size: 0.88rem;
  font-weight: 600;
  text-decoration: none;
  opacity: 0.95;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 1;
    color: #fff !important;
  }
}

.dn-hero__badge-wrap {
  flex-shrink: 0;
  text-align: right;
}

.dn-hero__badge {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.55rem 1.15rem;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.4);
  backdrop-filter: blur(8px);
}

.dn-hero__badge-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.95);
  box-shadow: 0 0 0 3px rgba(255, 255, 255, 0.25);
}

.dn-hero__badge-text {
  font-size: 0.9rem;
  font-weight: 700;
  color: #fff;
  letter-spacing: 0.02em;
}

.dn-hero__badge-range {
  margin: 0.65rem 0 0;
  font-size: 0.8rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.88);
  max-width: 260px;
  margin-left: auto;
  line-height: 1.35;

  &--muted {
    font-weight: 500;
    color: rgba(255, 255, 255, 0.82);
  }
}

@media (max-width: 767px) {
  .dn-hero {
    padding: 28px 22px;
  }

  .dn-hero__badge-wrap {
    text-align: left;
    width: 100%;
  }

  .dn-hero__badge-range {
    margin-left: 0;
  }
}

.dn-alert {
  border: none;
  border-radius: $dn-radius-sm;
  box-shadow: 0 4px 24px rgba(220, 53, 69, 0.12);
}

.dn-filters {
  padding: 1.25rem 1.35rem;
  margin-bottom: 1.75rem;
  background: rgba(255, 255, 255, 0.72);
  backdrop-filter: blur(10px);
  border: 1px solid $dn-line;
  border-radius: $dn-radius;
  box-shadow: 0 4px 24px rgba(91, 33, 182, 0.04);
}

.dn-label {
  display: block;
  font-size: 0.72rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: $dn-muted;
  margin-bottom: 0.4rem;
}

.dn-input {
  border-radius: 12px !important;
  border: 1px solid $dn-line !important;
  background: #fff;
  font-size: 0.9375rem;
  min-height: 42px;
}

::v-deep .dn-vselect .vs__dropdown-toggle {
  border-radius: 12px;
  border: 1px solid $dn-line;
  min-height: 42px;
  padding: 4px 8px;
  background: #fff;
}

.dn-btn-primary {
  border: none;
  border-radius: 12px;
  padding: 0.55rem 1.25rem;
  font-weight: 600;
  background: linear-gradient(135deg, #7c3aed 0%, #6366f1 48%, #8b5cf6 100%);
  box-shadow: 0 8px 24px rgba(124, 58, 237, 0.28);
  color: #fff !important;

  &:hover:not(:disabled) {
    filter: brightness(1.05);
    box-shadow: 0 10px 28px rgba(124, 58, 237, 0.35);
  }

  &:disabled {
    opacity: 0.65;
  }
}

.dn-empty {
  text-align: center;
  padding: 3rem 1.5rem;
  background: rgba(255, 255, 255, 0.55);
  border: 1px dashed $dn-line;
  border-radius: $dn-radius;
}

.dn-empty-icon {
  color: #a89bc4;
  opacity: 0.85;
}

.dn-empty-text {
  font-size: 0.95rem;
  color: $dn-muted;
  max-width: 26rem;
  margin-left: auto;
  margin-right: auto;
  line-height: 1.5;
}

.dn-kpi-section {
  margin-bottom: 1.25rem;

  &--secondary {
    margin-bottom: 1.75rem;
  }
}

.dn-kpi-section-title {
  margin: 0 0 0.65rem;
  font-size: 0.78rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: $dn-muted;
}

.dn-kpi-grid {
  display: grid;
  gap: 1rem;
  margin-bottom: 0;

  &--3 {
    grid-template-columns: repeat(3, 1fr);

    @media (max-width: 991px) {
      grid-template-columns: repeat(2, 1fr);
    }

    @media (max-width: 575px) {
      grid-template-columns: 1fr;
    }
  }
}

.dn-kpi-card {
  background: linear-gradient(180deg, #ffffff 0%, #fdfcff 100%);
  border: 1px solid $dn-line;
  border-radius: $dn-radius;
  box-shadow: $dn-shadow;
  padding: 1.25rem 1.35rem;
  min-height: 112px;
  transition: box-shadow 0.2s ease, transform 0.2s ease, border-color 0.2s ease;

  &:hover {
    box-shadow: 0 14px 48px rgba(91, 33, 182, 0.1);
    transform: translateY(-1px);
  }

  &--clickable {
    cursor: pointer;
    user-select: none;

    &:focus-visible {
      outline: 2px solid rgba(124, 58, 237, 0.45);
      outline-offset: 2px;
    }
  }

  &--expanded {
    min-height: auto;
    border-color: rgba(124, 58, 237, 0.28);
    box-shadow: 0 14px 40px rgba(91, 33, 182, 0.12);
  }

  &--official-blur {
    position: relative;
    overflow: hidden;

    &:hover {
      transform: none;
    }
  }
}

.dn-kpi-tap-hint {
  margin-top: 0.45rem;
  font-size: 0.68rem;
  font-weight: 500;
  color: #9c94b0;
  letter-spacing: 0.02em;
}

.dn-kpi-breakdown {
  margin-top: 0.85rem;
  padding-top: 0.85rem;
  border-top: 1px solid rgba(107, 100, 120, 0.1);
}

.dn-kpi-breakdown-row {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 0.75rem;
  font-size: 0.875rem;
  margin-bottom: 0.5rem;

  &:last-child {
    margin-bottom: 0;
  }
}

.dn-kpi-breakdown-label {
  color: $dn-muted;
  font-weight: 500;
}

.dn-kpi-breakdown-value {
  font-weight: 700;
  color: $dn-ink;
  font-variant-numeric: tabular-nums;
}

.dn-kpi-card-inner {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 0.75rem;
  height: 100%;

  &--blurred {
    filter: blur(7px);
    opacity: 0.55;
    pointer-events: none;
    user-select: none;
  }
}

.dn-official-only-overlay {
  position: absolute;
  inset: 0;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.85rem 1rem;
  text-align: center;
  background: rgba(255, 255, 255, 0.72);
  backdrop-filter: blur(2px);

  &__text {
    font-size: 0.8125rem;
    font-weight: 600;
    line-height: 1.45;
    color: $dn-ink;
    max-width: 15rem;
  }
}

.dn-kpi-label-row {
  display: flex;
  align-items: center;
  gap: 0.35rem;
  margin-bottom: 0.35rem;
}

.dn-kpi-label {
  font-size: 0.78rem;
  font-weight: 500;
  color: $dn-muted;
  letter-spacing: 0.01em;
}

.dn-kpi-help {
  color: #b8b0c9;
  cursor: help;
  flex-shrink: 0;
}

.dn-kpi-value-row {
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 0.35rem;
}

.dn-kpi-value {
  font-size: 1.55rem;
  font-weight: 700;
  color: $dn-ink;
  letter-spacing: -0.02em;
  line-height: 1.15;
}

.dn-kpi-delta {
  font-size: 0.75rem;
  font-weight: 600;
  color: #16a34a;
  background: rgba(22, 163, 74, 0.1);
  padding: 0.12rem 0.4rem;
  border-radius: 8px;
}

.dn-kpi-delta--inline {
  margin-left: 0.25rem;
}

.dn-kpi-icon-wrap {
  width: 48px;
  height: 48px;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: #5b21b6;

  &--green {
    background: rgba(34, 197, 94, 0.14);
    color: #15803d;
  }

  &--blue {
    background: rgba(99, 102, 241, 0.15);
    color: #4f46e5;
  }

  &--amber {
    background: rgba(245, 158, 11, 0.16);
    color: #c2410c;
  }
}

.dn-row-2 {
  margin-bottom: 1.75rem;
}

.dn-panel {
  background: rgba(255, 255, 255, 0.88);
  border: 1px solid $dn-line;
  border-radius: $dn-radius;
  box-shadow: $dn-shadow;
  padding: 1.35rem 1.4rem 1.5rem;
  height: 100%;

  &--compact {
    padding-bottom: 1.25rem;
  }

  &--carts-main {
    .dn-mini-cards {
      gap: 1rem;
    }

    @media (min-width: 1200px) {
      .dn-mini-cards {
        grid-template-columns: repeat(3, minmax(0, 1fr));
      }

      .dn-funil-spark {
        width: 160px;
        min-width: 140px;
        height: 60px;
      }
    }

    .dn-funnel-line {
      margin-bottom: 0.75rem;
      padding: 0.65rem 1rem;
      border-radius: $dn-radius-sm;
      background: rgba(243, 239, 255, 0.55);
      border: 1px solid rgba(107, 100, 120, 0.07);
    }
  }
}

.dn-block-title {
  font-size: 1.05rem;
  font-weight: 700;
  color: $dn-ink;
  margin-bottom: 1.15rem;
  letter-spacing: -0.02em;

  &--spaced {
    margin-bottom: 1rem;
  }
}

.dn-mini-cards {
  display: grid;
  grid-template-columns: 1fr 1fr 1.15fr;
  gap: 0.75rem;
  margin-bottom: 1rem;

  @media (max-width: 1199px) {
    grid-template-columns: 1fr 1fr;
  }

  @media (max-width: 575px) {
    grid-template-columns: 1fr;
  }
}

.dn-mini-card {
  border-radius: $dn-radius-sm;
  padding: 1rem 1.05rem;
  min-height: 88px;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;

  &--gradient {
    background: linear-gradient(135deg, #7c3aed 0%, #6366f1 42%, #4f46e5 100%);
    color: #fff;
    box-shadow: 0 10px 28px rgba(99, 102, 241, 0.25);
    border: none;

    .dn-mini-label {
      color: rgba(255, 255, 255, 0.82);
    }

    .dn-mini-value {
      color: #fff;
    }
  }

  &--light {
    background: linear-gradient(180deg, #ffffff 0%, $dn-lilac-0 100%);
    border: 1px solid $dn-line;
    box-shadow: 0 6px 20px rgba(91, 33, 182, 0.05);
  }

  &--clickable {
    cursor: pointer;
    user-select: none;
    transition: box-shadow 0.2s ease, transform 0.2s ease, border-color 0.2s ease;

    &:hover {
      box-shadow: 0 12px 28px rgba(91, 33, 182, 0.1);
      transform: translateY(-1px);
    }

    &:focus-visible {
      outline: 2px solid rgba(124, 58, 237, 0.45);
      outline-offset: 2px;
    }
  }
}

.dn-mini-label {
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  opacity: 0.9;
  margin-bottom: 0.35rem;

  &--dark {
    color: $dn-muted;
  }
}

.dn-mini-value {
  font-size: 1.35rem;
  font-weight: 700;
  letter-spacing: -0.02em;
}

.dn-mini-light-top {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 0.5rem;
  width: 100%;
}

.dn-funil-main {
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
}

.dn-funil-number {
  font-size: 1.5rem;
  font-weight: 800;
  color: $dn-ink;
}

.dn-funil-spark {
  width: 120px;
  min-width: 100px;
  height: 56px;
  flex-shrink: 0;
}

.dn-funnel-line {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem 0.75rem;
  font-size: 0.88rem;
  color: $dn-muted;
  margin-bottom: 0.35rem;

  strong {
    color: $dn-ink;
    font-weight: 700;
  }
}

.dn-funnel-sep {
  color: #c4bdd9;
  font-weight: 400;
}

.dn-funnel-hint {
  font-size: 0.7rem;
  color: #9c94b0;
  margin-bottom: 1rem;
  line-height: 1.35;
}

.dn-chart-area-wrap {
  border-radius: $dn-radius-sm;
  border: 1px solid $dn-line;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.97) 0%, $dn-lilac-0 100%);
  overflow: hidden;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.85);

  &--trend {
    margin-top: 0.35rem;
    border-radius: $dn-radius;
    border-color: rgba(124, 58, 237, 0.14);
    box-shadow:
      0 8px 32px rgba(91, 33, 182, 0.06),
      inset 0 1px 0 rgba(255, 255, 255, 0.9);
  }
}

.dn-chart-area-head {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.85rem 1rem 0.5rem;
  border-bottom: 1px solid rgba(107, 100, 120, 0.08);

  &--stack {
    flex-direction: column;
    align-items: stretch;
    gap: 0.75rem;
    padding: 1rem 1.15rem 0.9rem;
  }
}

.dn-chart-area-head-row {
  display: flex;
  flex-direction: column;
  gap: 0.28rem;
}

.dn-chart-area-title {
  font-size: 0.82rem;
  font-weight: 700;
  color: $dn-ink;
  letter-spacing: -0.02em;
}

.dn-chart-area-sub {
  font-size: 0.72rem;
  font-weight: 500;
  color: $dn-muted;
  line-height: 1.4;
}

.dn-chart-area-body {
  padding: 0.5rem 0.65rem 0.85rem;
  min-height: 200px;

  &--trend {
    min-height: 268px;
    padding: 0.65rem 1rem 1rem;
  }
}

.dn-trend-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 0.45rem;
}

.dn-trend-chip {
  display: inline-flex;
  align-items: center;
  gap: 0.42rem;
  border: 1px solid rgba(107, 100, 120, 0.18);
  background: rgba(255, 255, 255, 0.88);
  border-radius: 999px;
  padding: 0.4rem 0.88rem;
  font-size: 0.72rem;
  font-weight: 600;
  color: $dn-muted;
  cursor: pointer;
  transition:
    border-color 0.15s ease,
    background 0.15s ease,
    color 0.15s ease,
    box-shadow 0.15s ease;

  &:focus {
    outline: none;
    box-shadow: 0 0 0 3px rgba(124, 58, 237, 0.22);
  }

  &:hover {
    border-color: rgba(124, 58, 237, 0.38);
    color: $dn-ink;
  }

  &--active {
    border-color: var(--trend-accent, #7c3aed);
    background: var(--trend-accent-soft, rgba(124, 58, 237, 0.14));
    color: $dn-ink;
    box-shadow: 0 3px 12px rgba(124, 58, 237, 0.1);
  }
}

.dn-trend-chip-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--trend-accent, rgba(156, 151, 173, 0.85));
  flex-shrink: 0;
}

.dn-trend-chip--active .dn-trend-chip-dot {
  box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.95);
}

.dn-trend-chip-text {
  line-height: 1.25;
  max-width: 220px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  @media (max-width: 575px) {
    max-width: 150px;
  }
}

.dn-muted-center {
  text-align: center;
  color: $dn-muted;
  font-size: 0.9rem;
}

.dn-insight {
  display: flex;
  gap: 0.85rem;
  align-items: flex-start;
  padding: 0.95rem 1rem;
  margin-bottom: 0.65rem;
  border-radius: $dn-radius-sm;
  background: rgba(255, 255, 255, 0.65);
  border: 1px solid rgba(107, 100, 120, 0.07);

  &:last-child {
    margin-bottom: 0;
  }
}

.dn-insight-dot {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  margin-top: 0.35rem;
  flex-shrink: 0;

  &--yellow {
    background: linear-gradient(135deg, #facc15, #eab308);
    box-shadow: 0 0 0 4px rgba(250, 204, 21, 0.2);
  }

  &--orange {
    background: linear-gradient(135deg, #fb923c, #f97316);
    box-shadow: 0 0 0 4px rgba(251, 146, 60, 0.18);
  }

  &--green {
    background: linear-gradient(135deg, #4ade80, #22c55e);
    box-shadow: 0 0 0 4px rgba(74, 222, 128, 0.2);
  }
}

.dn-insight-period {
  font-size: 0.72rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #9c94b0;
}

.dn-insight-empty {
  font-size: 0.84rem;
  color: $dn-muted;
  line-height: 1.45;
}

.dn-insight-text {
  font-size: 0.84rem;
  line-height: 1.45;
  color: #4a4458;
  margin: 0;
}

.dn-trans-block {
  margin-bottom: 1.75rem;
}

.dn-trans-meta-hint {
  display: flex;
  align-items: flex-start;
  gap: 0.55rem;
  margin-top: 1.1rem;
  padding: 0.85rem 1.05rem;
  font-size: 0.8125rem;
  line-height: 1.5;
  color: $dn-muted;
  background: rgba(243, 239, 255, 0.55);
  border: 1px solid rgba(107, 100, 120, 0.08);
  border-radius: $dn-radius-sm;
}

.dn-trans-meta-hint__icon {
  flex-shrink: 0;
  margin-top: 0.12rem;
  color: #7c3aed;
}

.dn-hero-chart {
  position: relative;
  border-radius: $dn-radius;
  overflow: hidden;
  min-height: 260px;
  background: linear-gradient(145deg, #4c1d95 0%, #5b21b6 35%, #4338ca 70%, #312e81 100%);
  box-shadow: 0 16px 48px rgba(76, 29, 149, 0.35);
}

.dn-hero-chart-overlay {
  position: relative;
  z-index: 2;
  padding: 1.35rem 1.4rem 0.5rem;
}

.dn-hero-metrics {
  display: flex;
  flex-wrap: wrap;
  gap: 1.25rem 1.75rem;
}

.dn-hero-metric-label {
  display: block;
  font-size: 0.68rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.07em;
  color: rgba(255, 255, 255, 0.62);
  margin-bottom: 0.25rem;
}

.dn-hero-metric-value {
  font-size: 1.45rem;
  font-weight: 800;
  color: #fff;
  letter-spacing: -0.02em;
}

.dn-hero-metric-sub {
  display: block;
  margin-top: 0.2rem;
  font-size: 0.72rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.78);
  line-height: 1.3;
  max-width: 11rem;
}

.dn-hero-chart-canvas {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  height: 72%;
  z-index: 1;
  opacity: 0.95;
}

.dn-hero-chart-canvas-stack {
  position: absolute;
  inset: 0;

  &--blurred {
    filter: blur(10px);
    opacity: 0.45;
    pointer-events: none;
  }
}

.dn-hero-chart-official-mask {
  position: absolute;
  inset: 0;
  z-index: 3;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem 1.25rem;
  text-align: center;
  font-size: 0.875rem;
  font-weight: 600;
  line-height: 1.45;
  color: #fff;
  background: rgba(17, 24, 39, 0.5);
  backdrop-filter: blur(2px);
}

.dn-table-card {
  background: rgba(255, 255, 255, 0.95);
  border: 1px solid $dn-line;
  border-radius: $dn-radius;
  box-shadow: $dn-shadow;
  overflow: hidden;
  height: 100%;
  min-height: 260px;
  display: flex;
  flex-direction: column;
}

.dn-table-official-hint {
  margin: 0.65rem 0 0;
  font-size: 0.78rem;
  font-weight: 500;
  line-height: 1.45;
  color: $dn-muted;
  max-width: 42rem;
}

.dn-table-head {
  padding: 1.1rem 1.35rem 0.85rem;
  border-bottom: 1px solid rgba(107, 100, 120, 0.08);
}

.dn-table-title {
  font-size: 1rem;
  font-weight: 700;
  color: $dn-ink;
  display: block;
}

.dn-table-caption {
  font-size: 0.75rem;
  color: $dn-muted;
  margin-top: 0.2rem;
  display: block;
}

.dn-table-scroll {
  overflow-x: auto;
  flex: 1;
}

.dn-table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  font-size: 0.8125rem;

  th {
    text-align: left;
    font-weight: 600;
    font-size: 0.68rem;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: $dn-muted;
    padding: 0.65rem 1rem;
    background: rgba(250, 248, 255, 0.9);
    border-bottom: 1px solid $dn-line;
    white-space: nowrap;

    &:first-child {
      min-width: 11rem;
    }
  }

  td {
    padding: 0.75rem 1rem;
    border-bottom: 1px solid rgba(107, 100, 120, 0.06);
    color: #3d364d;
    vertical-align: middle;
  }

  tbody tr {
    cursor: pointer;
    transition: background 0.15s ease;
    background: #fff;

    &:hover {
      background: rgba(124, 58, 237, 0.04);
    }

    &.dn-tr--active {
      background: rgba(124, 58, 237, 0.08) !important;

      td {
        box-shadow: inset 3px 0 0 0 #7c3aed;
      }
    }
  }

  tfoot td {
    background: rgba(243, 239, 255, 0.85);
    border-bottom: none;
    padding-top: 0.95rem;
    padding-bottom: 0.95rem;
    font-size: 0.875rem;
  }
}

.dn-t-right {
  text-align: right;
}

.dn-t-num {
  font-variant-numeric: tabular-nums;
  font-feature-settings: 'tnum';
}

.dn-t-strong {
  font-weight: 700;
  color: $dn-ink;
}

.dn-status-cell {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  white-space: nowrap;
}

.dn-status-dot {
  flex-shrink: 0;
  display: block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
}

.dn-dot--green {
  background: #22c55e;
  box-shadow: 0 0 0 3px rgba(34, 197, 94, 0.2);
}

.dn-dot--orange {
  background: #f97316;
  box-shadow: 0 0 0 3px rgba(249, 115, 22, 0.2);
}

.dn-dot--red {
  background: #ef4444;
  box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.2);
}

.dn-dot--blue {
  background: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.2);
}

.dn-dot--teal {
  background: #14b8a6;
  box-shadow: 0 0 0 3px rgba(20, 184, 166, 0.2);
}

.dn-dot--purple {
  background: #8b5cf6;
  box-shadow: 0 0 0 3px rgba(139, 92, 246, 0.2);
}

.dn-dot--slate {
  background: #94a3b8;
}

.dn-status-label {
  line-height: 1.25;
}

.dn-history {
  margin-top: 0.25rem;

  ::v-deep .b-table {
    font-size: 0.8125rem;
  }

  ::v-deep .table {
    border-radius: $dn-radius-sm;
    overflow: hidden;
  }

  ::v-deep .table th {
    border-top: none;
    font-weight: 600;
    font-size: 0.72rem;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: $dn-muted;
  }
}
</style>

<style lang="scss">
@import '@core/scss/vue/libs/chart-apex.scss';
@import '@core/scss/vue/libs/vue-flatpicker.scss';
</style>
